/* LOFTfifty6 — bankconfigurator voor modulaire banken (SEVN + Passe Partout).

   Uitgangspunten:
   - Alle model-, element- en stofgegevens komen uit metaobjects en staan als JSON
     in de pagina (snippet lf-bank-data). In dit bestand staat geen enkele
     productnaam, prijs, stofnaam of elementcode.
   - De prijs komt uitsluitend uit de Shopify-varianten. Er wordt nooit een prijs
     berekend of verzonnen; ontbreekt een variant, dan is het element niet te
     kiezen en verschijnt een beheerdersmelding.
   - Toevoegen aan de winkelwagen gebeurt met variant-id's, zodat Shopify de
     prijs serverside opnieuw vaststelt.
   - Geen externe libraries.
*/

(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Hulpfuncties
   * ------------------------------------------------------------------ */

  var normaliseer = function (waarde) {
    return String(waarde == null ? '' : waarde)
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  };

  var geld = function (centen) {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format((Number(centen) || 0) / 100);
  };

  var getal = function (waarde) {
    var n = parseFloat(String(waarde).replace(',', '.'));
    return isFinite(n) && n > 0 ? n : 0;
  };

  var maak = function (tag, klasse, tekst) {
    var el = document.createElement(tag);
    if (klasse) el.className = klasse;
    if (tekst != null) el.textContent = tekst;
    return el;
  };

  var esc = function (waarde) {
    return String(waarde == null ? '' : waarde).replace(/[&<>"']/g, function (t) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[t];
    });
  };

  /* Een lijstveld uit Shopify komt als lijst binnen wanneer het metaveld het juiste
     type heeft, en als tekst wanneer dat niet zo is. Beide gevallen leveren hier
     een lijst op; anders liep de opbouw van de voorbeeldopstellingen vast op een
     TypeError en werd stap 1 helemaal niet meer getekend. */
  var naarLijst = function (waarde) {
    if (Array.isArray(waarde)) return waarde;
    if (waarde == null || waarde === '') return [];
    var tekst = String(waarde).trim();
    if (tekst.charAt(0) === '[') {
      try {
        var gelezen = JSON.parse(tekst);
        if (Array.isArray(gelezen)) return gelezen;
      } catch (e) { /* geen geldige JSON: hieronder als kommalijst behandelen */ }
    }
    return tekst.split(',');
  };

  /* ------------------------------------------------------------------ *
   * Centrale foutregistratie
   *
   * Eén plek waar technische fouten heen gaan. Nooit losse console-regels door
   * de code heen, en nooit een technische tekst richting de klant: die krijgt
   * een begrijpelijke Nederlandse melding, de ontwikkelaar krijgt de details.
   *
   * Er wordt bewust niets vastgelegd dat naar een persoon herleidbaar is: geen
   * namen, e-mailadressen, telefoonnummers of klantopmerkingen. Van een URL
   * blijven alleen pad en de functionele parameters over; previewtokens en
   * sleutels worden verwijderd.
   * ------------------------------------------------------------------ */

  var VERSIE = '2026-07-29';
  var GEVOELIGE_PARAMETERS = ['preview_theme_id', 'key', 'token', 'signature', 'sig',
                              '_ab', '_fd', '_sc', 'access_token', 'password'];

  var schoonUrl = function (url) {
    try {
      var u = new URL(String(url), window.location.origin);
      GEVOELIGE_PARAMETERS.forEach(function (p) { u.searchParams.delete(p); });
      return u.pathname + (u.search || '');
    } catch (e) {
      return String(url).split('?')[0];
    }
  };

  var logboek = [];

  /* fase: laden | prijs | variant | winkelwagen | herstel */
  var meldFout = function (soort, fase, details) {
    var d = details || {};
    var regel = {
      soort: soort,
      fase: fase,
      tijd: new Date().toISOString(),
      versie: VERSIE,
      bankprogramma: d.bankprogramma || null,
      configuratieId: d.configuratieId || null,
      elementcode: d.elementcode || null,
      stofgroep: d.stofgroep || null,
      variantsleutel: d.variantsleutel || null,
      httpStatus: d.httpStatus == null ? null : d.httpStatus,
      url: d.url ? schoonUrl(d.url) : null,
      toelichting: d.toelichting || null
    };
    logboek.push(regel);
    if (logboek.length > 200) logboek.shift();
    /* In DEV uitgebreid; een productieversie kan dit terugbrengen tot alleen
       verzamelen zonder uitvoer. */
    if (window.console && console.warn) console.warn('[lf-bank]', regel);
    return regel;
  };

  window.LF_BANK = window.LF_BANK || {};
  window.LF_BANK.logboek = function () { return logboek.slice(); };
  window.LF_BANK.versie = VERSIE;

  /* Compacte codering van een configuratie voor de deel-URL. */
  var codeer = function (waarde) {
    try {
      var bytes = new TextEncoder().encode(JSON.stringify(waarde));
      var binair = '';
      bytes.forEach(function (b) { binair += String.fromCharCode(b); });
      return btoa(binair).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch (e) { return ''; }
  };

  var decodeer = function (waarde) {
    try {
      var genormaliseerd = String(waarde).replace(/-/g, '+').replace(/_/g, '/');
      var aangevuld = genormaliseerd + '='.repeat((4 - (genormaliseerd.length % 4)) % 4);
      var binair = atob(aangevuld);
      var bytes = Uint8Array.from(binair, function (t) { return t.charCodeAt(0); });
      return JSON.parse(new TextDecoder().decode(bytes));
    } catch (e) { return null; }
  };

  /* ------------------------------------------------------------------ *
   * Technische tekening — één stijl voor alle elementen
   *
   * Alles wordt in centimeters getekend en pas bij het plaatsen geschaald.
   * De buitenmaat van elk element is exact de opgegeven breedte x diepte.
   * De banden voor rug en armleuning zijn een vaste tekenconventie en geen
   * opgegeven maat; dat staat ook zo bij de tekening vermeld.
   * ------------------------------------------------------------------ */

  var LIJN = '#4A3C31';
  var VLAK = '#F2EDE6';
  var RUGVLAK = '#E4DACE';
  var DIEP = '#2A211B';

  var bandRug = function (diepte) { return Math.max(11, Math.min(24, diepte * 0.24)); };
  var bandArm = function (breedte) { return Math.max(10, Math.min(26, breedte * 0.19)); };

  /* Bouwt de onderdelen van één element op in cm-ruimte (0,0 = linksachter).
     De banden voor rug en armleuning zijn een tekenconventie en geen opgegeven
     maat; de buitenmaat van het element is wel exact breedte x diepte. */
  var tekenElement = function (element, vullingen) {
    var v = typeof vullingen === 'string'
      ? { zit: vullingen, rug: RUGVLAK, arm: RUGVLAK }
      : (vullingen || { zit: VLAK, rug: RUGVLAK, arm: RUGVLAK });

    var b = getal(element.b) || 90;
    var d = getal(element.d) || 90;
    var vorm = element.vorm || 'zit';
    var stukken = [];

    var vlak = function (x, y, w, h, fill, r) {
      if (w <= 0.5 || h <= 0.5) return;
      stukken.push('<rect x="' + x.toFixed(1) + '" y="' + y.toFixed(1) +
        '" width="' + w.toFixed(1) + '" height="' + h.toFixed(1) +
        '" rx="' + (r == null ? 2 : r) + '" fill="' + fill +
        '" stroke="' + LIJN + '" stroke-width="1.1"/>');
    };

    /* Kussen met reliëf: lichtrand boven, schaduwrand onder. Dat maakt van een
       plat vlak een zichtbaar volume zonder dat de contour verandert. */
    var kussen = function (x, y, w, h, fill, r) {
      if (w <= 1 || h <= 1) return;
      var rad = r == null ? 3 : r;
      stukken.push('<rect x="' + (x + 1).toFixed(1) + '" y="' + (y + 2).toFixed(1) +
        '" width="' + w.toFixed(1) + '" height="' + h.toFixed(1) +
        '" rx="' + rad + '" fill="' + DIEP + '" opacity="0.13"/>');
      stukken.push('<rect x="' + x.toFixed(1) + '" y="' + y.toFixed(1) +
        '" width="' + w.toFixed(1) + '" height="' + h.toFixed(1) +
        '" rx="' + rad + '" fill="' + fill + '" stroke="' + LIJN + '" stroke-width="1"/>');
      stukken.push('<rect x="' + (x + 1.8).toFixed(1) + '" y="' + (y + 1.8).toFixed(1) +
        '" width="' + Math.max(1, w - 3.6).toFixed(1) + '" height="' + Math.max(1, h - 3.6).toFixed(1) +
        '" rx="' + Math.max(1, rad - 1) + '" fill="none" stroke="#FFFFFF" stroke-width="1.7" opacity="0.34"/>');
      stukken.push('<rect x="' + (x + 0.7).toFixed(1) + '" y="' + (y + 0.7).toFixed(1) +
        '" width="' + Math.max(1, w - 1.4).toFixed(1) + '" height="' + Math.max(1, h - 1.4).toFixed(1) +
        '" rx="' + rad + '" fill="none" stroke="' + DIEP + '" stroke-width="1.1" opacity="0.18"/>');
    };

    /* Slagschaduw onder het hele element. */
    stukken.push('<rect x="3" y="4" width="' + b.toFixed(1) + '" height="' + d.toFixed(1) +
      '" rx="3" fill="' + DIEP + '" opacity="0.11"/>');

    var rug = bandRug(d);
    var arm = bandArm(b);
    var armLinks = vorm === 'zit-arm-links' || vorm === 'zit-twee-armen' ||
                   vorm === 'longchair-links' || vorm === 'fauteuil';
    var armRechts = vorm === 'zit-arm-rechts' || vorm === 'zit-twee-armen' ||
                    vorm === 'longchair-rechts' || vorm === 'fauteuil';

    if (vorm === 'poef') {
      vlak(0, 0, b, d, v.arm, 3);
      kussen(3, 3, b - 6, d - 6, v.zit, 3);
      return { onderdelen: stukken, b: b, d: d };
    }

    if (vorm === 'rond') {
      stukken.push('<ellipse cx="' + (b / 2 + 1.5).toFixed(1) + '" cy="' + (d / 2 + 2).toFixed(1) +
        '" rx="' + (b / 2 - 0.6).toFixed(1) + '" ry="' + (d / 2 - 0.6).toFixed(1) +
        '" fill="' + DIEP + '" opacity="0.12"/>');
      stukken.push('<ellipse cx="' + (b / 2).toFixed(1) + '" cy="' + (d / 2).toFixed(1) +
        '" rx="' + (b / 2 - 0.6).toFixed(1) + '" ry="' + (d / 2 - 0.6).toFixed(1) +
        '" fill="' + v.zit + '" stroke="' + LIJN + '" stroke-width="1.1"/>');
      stukken.push('<ellipse cx="' + (b / 2).toFixed(1) + '" cy="' + (d / 2).toFixed(1) +
        '" rx="' + (b / 2 - 3).toFixed(1) + '" ry="' + (d / 2 - 3).toFixed(1) +
        '" fill="none" stroke="#FFFFFF" stroke-width="1.7" opacity="0.30"/>');
      return { onderdelen: stukken, b: b, d: d };
    }

    /* Frame van het element. */
    vlak(0, 0, b, d, v.arm, 3);

    /* Hoekelement: rug langs de bovenkant én langs één zijkant. */
    if (vorm === 'hoek' || vorm === 'hoek-links' || vorm === 'hoek-rechts') {
      var zijLinks = vorm === 'hoek-links';
      kussen(zijLinks ? arm : 0, 0, b - arm, rug, v.rug, 3);
      if (zijLinks) kussen(0, 0, arm, d, v.rug, 3);
      else kussen(b - arm, 0, arm, d, v.rug, 3);
      var zx = zijLinks ? arm : 0;
      kussen(zx + 2, rug + 2, b - arm - 4, d - rug - 4, v.zit, 3);
      return { onderdelen: stukken, b: b, d: d };
    }

    var zitX = armLinks ? arm : 0;
    var zitB = b - (armLinks ? arm : 0) - (armRechts ? arm : 0);

    /* Rugkussens, in hetzelfde aantal als de zitkussens. */
    var aantal = Math.max(1, Math.round(zitB / 78));
    var open = vorm === 'open-end-links' || vorm === 'open-end-rechts';
    var rugX = zitX;
    var rugB = zitB;
    if (open) {
      var afronding = Math.min(b * 0.26, d * 0.45);
      if (vorm === 'open-end-links') { rugX = afronding; rugB = b - afronding; }
      else { rugX = 0; rugB = b - afronding; }
    }
    if (vorm === 'longchair-links' || vorm === 'longchair-rechts') {
      /* Longchair: rug alleen over het achterste deel, voorzijde blijft open. */
      kussen(rugX + 1, 1, rugB - 2, rug, v.rug, 3);
    } else {
      var rugGat = 1.6;
      var rugStuk = (rugB - rugGat * (aantal - 1)) / aantal;
      for (var r = 0; r < aantal; r++) {
        kussen(rugX + r * (rugStuk + rugGat) + 1, 1, rugStuk - 2, rug, v.rug, 3);
      }
    }

    /* Armleuningen. */
    if (armLinks) kussen(1, 1, arm - 2, d - 2, v.arm, 3);
    if (armRechts) kussen(b - arm + 1, 1, arm - 2, d - 2, v.arm, 3);

    /* Zitkussens. */
    var zitY = rug + 2;
    var zitD = d - zitY - 2;
    var gat = 1.8;
    var stuk = (zitB - gat * (aantal - 1) - 4) / aantal;
    for (var i = 0; i < aantal; i++) {
      kussen(zitX + 2 + i * (stuk + gat), zitY, stuk, zitD, v.zit, 3);
    }

    /* Open eindelement: ronde buitenrand. */
    if (open) {
      var links = vorm === 'open-end-links';
      var ax = links ? Math.min(b * 0.26, d * 0.45) : b - Math.min(b * 0.26, d * 0.45);
      stukken.push('<path d="M ' + ax.toFixed(1) + ' 0.8 A ' +
        Math.min(b * 0.26, d * 0.45).toFixed(1) + ' ' + (d / 2).toFixed(1) + ' 0 0 ' + (links ? 0 : 1) + ' ' +
        ax.toFixed(1) + ' ' + (d - 0.8).toFixed(1) + '" fill="none" stroke="' + LIJN +
        '" stroke-width="1" opacity="0.5"/>');
    }

    return { onderdelen: stukken, b: b, d: d };
  };

  /* Losse SVG van één element, voor kaarten en overzichtsregels. */
  var elementSvg = function (element, vullingen, hoogte, defs) {
    if (element.illustratie) {
      return '<img src="' + esc(element.illustratie) + '" alt="' + esc(element.naam) +
             '" loading="lazy" decoding="async">';
    }
    var t = tekenElement(element, vullingen);
    var marge = 5;
    return '<svg viewBox="' + (-marge) + ' ' + (-marge) + ' ' + (t.b + marge * 2 + 3) + ' ' +
           (t.d + marge * 2 + 4) + '" role="img" aria-label="' + esc(element.naam) +
           ', bovenaanzicht" ' + (hoogte ? 'height="' + hoogte + '"' : '') + '>' +
           (defs || '') + t.onderdelen.join('') + '</svg>';
  };

  var isHoek = function (element) {
    return element.vorm === 'hoek' || element.vorm === 'hoek-links' || element.vorm === 'hoek-rechts';
  };

  /* Niet elk hoekelement laat de rij afbuigen, en dat is geen aanname maar volgt
     uit de tekening. Bij 'hoek' en 'hoek-rechts' loopt de rug langs de bovenkant
     én langs de rechterzijde: de rij komt van links en gaat bij dit element de
     hoek om naar voren. Bij 'hoek-links' loopt de rug langs de bovenkant en de
     linkerzijde; het afbuigende deel zit dan links in het element zelf, binnen de
     eigen buitenmaat, en de rij loopt daarna gewoon door naar rechts. Zonder dit
     onderscheid draaiden beide spiegelversies dezelfde kant op en was een L-bank
     naar de andere zijde niet samen te stellen. */
  var buigtAf = function (element) {
    return element.vorm === 'hoek' || element.vorm === 'hoek-rechts';
  };

  var HOEKEN = [0, 90, 180, 270];

  var bouwOpstelling = function (elementen) {
    var richting = 0;
    var lx = 0, ly = 0;   /* aanzetpunt van het volgende element; ook het draaipunt */
    var geplaatst = [];
    var minX = 0, minY = 0, maxX = 0, maxY = 0;

    elementen.forEach(function (element) {
      var b = getal(element.b);
      var d = getal(element.d);
      if (!b || !d) { geplaatst.push({ element: element, zonderMaat: true }); return; }

      /* De tekening draait om het aanzetpunt. Daardoor valt de rechthoek die het
         element werkelijk inneemt per looprichting op een andere plek. Deze
         rechthoek is alleen voor de omhullende maat; het element zelf wordt
         getekend met translate(aanzetpunt) + rotate(hoek). */
      var rx, ry, rw, rh;
      if (richting === 0)      { rx = lx;     ry = ly;     rw = b; rh = d; }
      else if (richting === 1) { rx = lx - d; ry = ly;     rw = d; rh = b; }
      else if (richting === 2) { rx = lx - b; ry = ly - d; rw = b; rh = d; }
      else                     { rx = lx;     ry = ly - b; rw = d; rh = b; }

      geplaatst.push({ element: element, x: lx, y: ly, hoek: HOEKEN[richting], b: b, d: d });

      minX = Math.min(minX, rx); minY = Math.min(minY, ry);
      maxX = Math.max(maxX, rx + rw); maxY = Math.max(maxY, ry + rh);

      if (buigtAf(element)) {
        /* Na een hoek buigt de opstelling een kwartslag af. Het volgende element
           zet aan op de buitenhoek van dit hoekelement, gezien in de nieuwe
           richting, zodat de twee delen netjes op elkaar aansluiten. */
        richting = (richting + 1) % 4;
        if (richting === 1)      { lx = rx + rw; ly = ry + rh; }
        else if (richting === 2) { lx = rx;      ly = ry + rh; }
        else if (richting === 3) { lx = rx;      ly = ry; }
        else                     { lx = rx + rw; ly = ry; }
      } else {
        /* Verder langs de lengterichting van de rij. */
        if (richting === 0)      lx += b;
        else if (richting === 1) ly += b;
        else if (richting === 2) lx -= b;
        else                     ly -= b;
      }
    });

    return { geplaatst: geplaatst, minX: minX, minY: minY, maxX: maxX, maxY: maxY };
  };

  /* ------------------------------------------------------------------ *
   * Configurator
   * ------------------------------------------------------------------ */

  /* Per aangezette configurator wordt bijgehouden wat er bij het verwijderen van de
     sectie weer moet worden losgekoppeld. In de themabewerker wordt een sectie bij
     elke opslagactie opnieuw geladen; zonder dit blijven luisteraars en tijdklokken
     achter. Dat is nu nog hinderlijk en wordt met een 3D-weergave erbij een echt
     probleem, omdat een gelekte WebGL-context een zwart vlak oplevert. */
  var opruimers = [];

  var start = function (wortel) {
    if (wortel.dataset.lfBankKlaar === 'true') return;
    wortel.dataset.lfBankKlaar = 'true';

    var opruimTaken = [];
    opruimers.push({
      wortel: wortel,
      uitvoeren: function () {
        opruimTaken.forEach(function (taak) {
          try { taak(); } catch (e) { /* opruimen mag nooit de rest blokkeren */ }
        });
        wortel.dataset.lfBankKlaar = 'false';
      }
    });

    /* Elke luisteraar op een blijvend element wordt geregistreerd, zodat het
       opruimen hem ook echt weghaalt. Zonder dit blijven bij een herstart op
       dezelfde DOM de oude luisteraars hangen en reageren er twee configurators
       tegelijk op één klik — met een bestelling die langs de blokkade van de
       nieuwe instantie glipt. */
    var luister = function (element, soort, fn) {
      if (!element) return;
      element.addEventListener(soort, fn);
      opruimTaken.push(function () { element.removeEventListener(soort, fn); });
    };

    var bron = wortel.querySelector('[data-lf-bank-data]');
    if (!bron) return;
    var data;
    try { data = JSON.parse(bron.textContent); } catch (e) { return; }

    /* De afbeeldingen staan als bestandsnaam in het JSON-bestand. De basis-URL
       van de Shopify-bestandsmap wordt afgeleid uit een stof die de snippet wél
       heeft geleverd, zodat er nergens een vaste CDN-URL in de code staat. */
    var bestandBasis = '';
    (function () {
      var metFoto = (data.stoffen || []).filter(function (s) { return s.foto; })[0];
      if (metFoto) bestandBasis = String(metFoto.foto).replace(/[^/]*$/, '');
    })();

    var el = function (naam) { return wortel.querySelector('[data-lf-' + naam + ']'); };
    var alle = function (naam) { return Array.prototype.slice.call(wortel.querySelectorAll('[data-lf-' + naam + ']')); };

    /* --- indexen -------------------------------------------------- */
    var elementenPerCode = {};
    data.elementen.forEach(function (e) { elementenPerCode[e.code] = e; });

    var variantSleutel = function (optiewaarde, groep) {
      return normaliseer(optiewaarde) + '||' + normaliseer(groep);
    };
    var varianten = {};
    data.varianten.forEach(function (v) { varianten[variantSleutel(v.e, v.g)] = v; });

    var variantVoor = function (element, groepcode) {
      if (!element || !groepcode) return null;
      return varianten[variantSleutel(element.optiewaarde, groepcode)] || null;
    };

    /* Een variant telt pas als geprijsd wanneer er een bedrag boven nul staat.
       Nul, leeg of een onleesbare waarde is een invoerfout in Shopify en mag
       nooit als geldige prijs door de configurator worden overgenomen. */
    var geldigePrijs = function (variant) {
      if (!variant) return false;
      var bedrag = Number(variant.prijs);
      return isFinite(bedrag) && bedrag > 0;
    };

    /* ------------------------------------------------------------------ *
     * Varianten voorbij de 250 die Liquid in één keer levert
     *
     * Liquid geeft per paginaverzoek maximaal 250 varianten. Een model met meer
     * combinaties verliest de rest zonder melding. De snippet gebruikt daarom
     * paginate; de server vertelt in variantenTotaal hoeveel er werkelijk zijn.
     * De resterende pagina's worden nagehaald via de Section Rendering API: dat
     * is dezelfde sectie, door dezelfde Liquid gerenderd, op hetzelfde domein.
     * Er komt dus geen tweede prijsbron bij en geen token.
     *
     * Zolang niet alle varianten binnen zijn mag de configurator een ontbrekende
     * combinatie niet verwarren met een combinatie die niet bestaat. Daarom drie
     * toestanden: laden, compleet, onvolledig. Alleen bij compleet kan er
     * besteld worden.
     * ------------------------------------------------------------------ */

    var variantStatus = 'compleet';
    var variantTekort = 0;

    var voegVariantenToe = function (lijst) {
      lijst.forEach(function (v) {
        var sleutel = variantSleutel(v.e, v.g);
        if (!varianten[sleutel]) {
          varianten[sleutel] = v;
          data.varianten.push(v);
        }
      });
    };

    var haalRestOpVarianten = function () {
      var totaal = Number(data.variantenTotaal);
      var paginas = Number(data.variantenPaginas) || 1;

      /* Alles al binnen — of het model past sowieso binnen één pagina. Dit moet
         de toestand expliciet op compleet zetten en niet alleen teruggeven:
         anders blijft een eerdere mislukking hangen en kan er na een geslaagde
         nieuwe poging nog steeds niet worden besteld. */
      if (!isFinite(totaal) || totaal <= data.varianten.length || paginas < 2) {
        if (variantStatus !== 'compleet') {
          variantStatus = 'compleet';
          variantTekort = 0;
          werkAlles();
        }
        return;
      }

      var sectie = wortel.closest('[id^="shopify-section-"]');
      if (!sectie) {
        variantStatus = 'onvolledig';
        variantTekort = totaal - data.varianten.length;
        return;
      }
      var sectieId = sectie.id.replace('shopify-section-', '');

      variantStatus = 'laden';
      variantTekort = totaal - data.varianten.length;
      werkAlles();

      var verzoeken = [];
      for (var p = 2; p <= paginas; p++) {
        verzoeken.push(
          fetch(window.location.pathname + '?section_id=' + encodeURIComponent(sectieId) + '&page=' + p,
                { headers: { Accept: 'text/html' } })
            .then(function (antwoord) {
              if (!antwoord.ok) {
                meldFout('variantpagina-niet-geladen', 'laden', {
                  bankprogramma: data.model, httpStatus: antwoord.status, url: antwoord.url,
                  toelichting: 'vervolgpagina met varianten gaf een foutstatus'
                });
                throw new Error('status ' + antwoord.status);
              }
              return antwoord.text();
            })
            .then(function (html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var script = doc.querySelector('[data-lf-bank-data]');
              if (!script) throw new Error('geen gegevens in het antwoord');
              var extra = JSON.parse(script.textContent);
              if (!extra || !Array.isArray(extra.varianten)) throw new Error('onleesbare gegevens');
              voegVariantenToe(extra.varianten);
            })
        );
      }

      Promise.all(verzoeken).then(function () {
        if (data.varianten.length >= totaal) {
          variantStatus = 'compleet';
          variantTekort = 0;
          /* Niet blind wissen: er kan een belangrijker bericht staan, zoals de
             vraag om zelf een stof te kiezen na een oude, niet-eenduidige link. */
          if (!stofOnduidelijk && meldingEl && !meldingEl.hidden &&
              meldingEl.getAttribute('data-soort') !== 'fout') {
            meld('');
          }
        } else {
          variantStatus = 'onvolledig';
          variantTekort = totaal - data.varianten.length;
        }
        werkAlles();
      }).catch(function (fout) {
        /* Nooit stil doorgaan: liever zichtbaar geblokkeerd dan een verkeerde of
           ontbrekende prijs. */
        meldFout('varianten-onvolledig', 'laden', {
          bankprogramma: data.model,
          toelichting: (fout && fout.message ? fout.message : 'onbekende fout') +
            ' — ' + data.varianten.length + ' van ' + totaal + ' geladen'
        });
        variantStatus = 'onvolledig';
        variantTekort = totaal - data.varianten.length;
        werkAlles();
      });
    };

    /* Staat er geen enkele variant op verkoopbaar, dan is dat een instelling in
       Shopify en geen keuze van de klant. Samenstellen blijft dan gewoon mogelijk;
       alleen bestellen is geblokkeerd, met een melding die de oorzaak benoemt. */
    var allesOnbeschikbaar = data.varianten.length > 0 &&
      !data.varianten.some(function (v) { return v.beschikbaar; });

    /* Liquid levert maximaal 250 varianten aan. Bij een groter programma
       ontbreken combinaties; dat mag nooit stil gebeuren. */
    var verwachteCombinaties = data.elementen.length * data.stofgroepen.length;

    /* Stoffen worden opgezocht op hun blijvende sleutel, nooit meer op kleurcode.
       SEVN nummert kleuren per collectie: code 2 wijst in groep C drie
       verschillende stoffen aan (Anthology Beige, Aphrodite Cream, Indiana
       Nougat). Opzoeken op code gaf daardoor stilzwijgend een andere stof terug. */
    /* Blijft leeg zolang de stof eenduidig is. Staat er een waarde in, dan kwam
       er een oude link binnen waarvan de stof niet te bepalen was; bestellen is
       dan geblokkeerd tot de klant zelf kiest. */
    var stofOnduidelijk = null;

    /* Een stofverwijzing uit een gedeelde link of de sessieopslag die bij het
       opstarten nog niet te vinden was. Blijft staan tot de stoffenbibliotheek
       binnen is; zolang hij staat is de samenstelling niet compleet. */
    var stofNogTeHerstellen = null;

    var stoffenPerSleutel = {};
    var stoffenPerHandle = {};
    var stoffenPerOudeSleutel = {};
    var indexeerStoffen = function () {
      stoffenPerSleutel = {};
      stoffenPerHandle = {};
      stoffenPerOudeSleutel = {};
      data.stoffen.forEach(function (s) {
        if (s.configurator_key) stoffenPerSleutel[s.configurator_key] = s;
        if (s.handle) stoffenPerHandle[s.handle] = s;
        if (s.oude_dev_key) stoffenPerOudeSleutel[s.oude_dev_key] = s;
      });
    };
    indexeerStoffen();

    /* ---------------------------------------------------------------- *
     * Stoffenbibliotheek uit het gegenereerde JSON-bestand
     *
     * Liquid lost van een metaobjectlijst maximaal 50 records op. Groep C telt
     * er 61. Het gegenereerde bestand bevat de volledige bibliotheek en wordt
     * eenmaal per sessie geladen. De metaobjecten blijven de beheerbron; dit
     * bestand is alleen publicatie.
     *
     * Faalt het laden of klopt de inhoud niet, dan wordt er NIET stil
     * teruggevallen op de onvolledige lijst: de betrokken stofgroepen worden
     * geblokkeerd met een melding.
     * ---------------------------------------------------------------- */

    var SCHEMA_ONDERSTEUND = 1;
    var stoffenBron = 'liquid';
    var geblokkeerdeGroepen = {};

    /* De vijf voorwaarden. Alle vijf moeten kloppen voordat er ook maar een stof
       uit het bestand wordt gebruikt. Voorwaarde 1 en 2 zijn hier al te toetsen,
       3 tot 5 pas na het ophalen. */
    var pilotConfig = data.pilotConfig || {};
    var pilotThemaOk = !!(pilotConfig.thema && String(pilotConfig.thema) === String(data.pilotThema));
    var pilotConfigAan = pilotConfig.aan === true || data.pilotSectieVinkje === true;
    var pilotAan = pilotThemaOk && pilotConfigAan;

    var laadStoffenBibliotheek = function () {
      /* Voorwaarde 1 en 2. Zonder deze twee wordt er geen enkel verzoek gedaan. */
      if (!pilotAan || !data.pilotBestand) return Promise.resolve();
      return fetch(data.pilotBestand, { headers: { Accept: 'application/json' } })
        .then(function (antwoord) {
          if (!antwoord.ok) throw new Error('status ' + antwoord.status);
          return antwoord.json();
        })
        .then(function (bib) {
          /* Voorwaarde 4: de schemaversie moet kloppen met wat de thema-lokale
             configuratie verwacht, niet alleen met wat de code kent. Zo bepaalt
             het thema zelf welke versie het aankan. */
          var verwacht = pilotConfig;
          var verwachteSchema = Number(verwacht.schemaVersie) || SCHEMA_ONDERSTEUND;
          if (Number(bib.schemaVersie) !== verwachteSchema) {
            throw new Error('schemaversie ' + bib.schemaVersie + ' wijkt af van de verwachte ' + verwachteSchema);
          }
          if (!Array.isArray(bib.stoffen) || bib.stoffen.length !== Number(bib.totaal)) {
            throw new Error('aantal klopt niet: ' + (bib.stoffen || []).length + ' tegen ' + bib.totaal);
          }
          /* Telling per groep moet overeenkomen met wat het bestand zelf opgeeft;
             anders is er onderweg iets afgekapt. */
          var geteld = {};
          bib.stoffen.forEach(function (x) { geteld[x.sleutel] = (geteld[x.sleutel] || 0) + 1; });
          var afwijkend = Object.keys(bib.perGroep || {}).filter(function (k) {
            return Number(bib.perGroep[k]) !== (geteld[k] || 0);
          });
          if (afwijkend.length) throw new Error('telling wijkt af bij ' + afwijkend.join(', '));

          /* Voorwaarde 5: het aantal per groep moet kloppen met wat de
             thema-lokale configuratie verwacht. Het bestand mag niet zelf
             bepalen wat compleet is; anders is een afgekapt bestand dat intern
             consistent is nog steeds goed genoeg, en dat is precies het probleem
             dat we oplossen. */
          var verwachtePerGroep = String(verwacht.groepsaantallen || '').split(',');
          var mismatch = [];
          verwachtePerGroep.forEach(function (paar) {
            var delen = paar.split(':');
            if (delen.length !== 2) return;
            var sleutel = delen[0].trim();
            var aantal = Number(delen[1]);
            if (!isFinite(aantal)) return;
            if ((geteld[sleutel] || 0) !== aantal) {
              mismatch.push(sleutel + ' verwacht ' + aantal + ', geleverd ' + (geteld[sleutel] || 0));
            }
          });
          if (mismatch.length) throw new Error('groepsaantal wijkt af: ' + mismatch.join('; '));

          /* Alleen de groepen van dit merk overnemen, en alleen die groepen die
             ook in het bankprogramma staan. */
          var eigenGroepen = data.stofgroepen.map(function (g) { return g.code; });
          var nieuw = bib.stoffen.filter(function (x) {
            return x.merk === data.merk && eigenGroepen.indexOf(x.stofgroep) > -1;
          }).map(function (x) {
            return {
              configurator_key: x.configurator_key,
              oude_dev_key: x.oude_dev_key,
              id: x.id,
              handle: x.handle,
              merk: x.merk,
              code: x.code,
              naam: x.naam,
              collectie: x.collectie,
              kleurnaam: x.kleurnaam,
              kleurfamilie: x.kleurfamilie,
              uitstraling: x.uitstraling,
              groep: x.stofgroep,
              positie: x.positie,
              foto: x.afbeelding ? bestandBasis + x.afbeelding : '',
              uitlopend: x.uitlopend === true
            };
          });

          if (!nieuw.length) throw new Error('geen stoffen voor dit merk in het bestand');

          /* Groepen die in het bestand zitten worden volledig vervangen; groepen
             die er niet in staan blijven uit de snippet komen. */
          var vervangen = {};
          nieuw.forEach(function (x) { vervangen[x.groep] = true; });
          data.stoffen = data.stoffen
            .filter(function (x) { return !vervangen[x.groep]; })
            .concat(nieuw.sort(function (a, b) {
              if (a.groep !== b.groep) return a.groep < b.groep ? -1 : 1;
              return (a.positie || 0) - (b.positie || 0);
            }));
          indexeerStoffen();
          stoffenBron = 'json';

          /* Tweede kans voor een stof uit een gedeelde link die bij het opstarten
             nog niet bestond. Zonder dit blijft een link naar stof 51 tot en met
             61 leeg, want die stonden niet in de eerste vijftig. */
          if (stofNogTeHerstellen) {
            var alsnog = zoekStof(stofNogTeHerstellen);
            if (alsnog.stof) {
              staat.stof = alsnog.stof.configurator_key || alsnog.stof.handle;
              stofNogTeHerstellen = null;
              meld('');
            } else if (alsnog.reden === 'code-meervoudig') {
              stofOnduidelijk = {
                code: (typeof stofNogTeHerstellen === 'string' ? stofNogTeHerstellen : stofNogTeHerstellen.code),
                aantal: alsnog.kandidaten.length,
                namen: alsnog.kandidaten.map(function (k) { return k.naam; })
              };
              stofNogTeHerstellen = null;
            } else {
              stofNogTeHerstellen = null;
              meldFout('stof-uit-link-niet-gevonden', 'herstel', {
                bankprogramma: data.model, stofgroep: staat.groep,
                toelichting: 'de stof uit de gedeelde link bestaat niet meer'
              });
              meld('De stof uit deze link wordt niet meer gevoerd. Kies zelf een kleur; ' +
                   'bestellen is geblokkeerd tot dat is gebeurd.', 'let');
            }
          }
          werkAlles();
        })
        .catch(function (f) {
          /* Elke groep die volgens de snippet onvolledig is, wordt geblokkeerd. */
          Object.keys(data.stoffenOpgegeven || {}).forEach(function (code) {
            var opgegeven = Number(data.stoffenOpgegeven[code]) || 0;
            var geladen = data.stoffen.filter(function (x) { return x.groep === code; }).length;
            if (opgegeven > geladen) geblokkeerdeGroepen[code] = opgegeven + ' van ' + geladen;
          });
          meldFout('stoffenbibliotheek-niet-geladen', 'laden', {
            bankprogramma: data.model, url: data.pilotBestand,
            toelichting: f && f.message ? f.message : 'onbekende fout'
          });
          werkAlles();
        });
    };

    /* Een stof terugvinden uit wat een gedeelde link of de sessieopslag aandraagt.
       In volgorde van betrouwbaarheid: blijvende sleutel, dan handle, dan als
       laatste redmiddel de kleurcode — en die alleen wanneer er precies één
       stof mee overeenkomt. */
    var zoekStof = function (verwijzing) {
      if (!verwijzing) return { stof: null, reden: 'leeg' };
      if (typeof verwijzing === 'string') verwijzing = { code: verwijzing };

      if (verwijzing.sleutel && stoffenPerSleutel[verwijzing.sleutel]) {
        return { stof: stoffenPerSleutel[verwijzing.sleutel], reden: 'sleutel' };
      }
      /* Tijdelijk: testlinks van vóór de canonieke sleutel dragen nog
         sevn-c-<id>. Die worden herkend en omgezet. */
      if (verwijzing.sleutel && stoffenPerOudeSleutel[verwijzing.sleutel]) {
        return { stof: stoffenPerOudeSleutel[verwijzing.sleutel], reden: 'oude-dev-sleutel' };
      }
      if (verwijzing.handle && stoffenPerHandle[verwijzing.handle]) {
        return { stof: stoffenPerHandle[verwijzing.handle], reden: 'handle' };
      }
      if (verwijzing.id) {
        var opId = data.stoffen.filter(function (s) { return s.id === verwijzing.id; });
        if (opId.length === 1) return { stof: opId[0], reden: 'id' };
      }
      if (verwijzing.code) {
        var groep = verwijzing.groep || staat.groep;
        var kandidaten = data.stoffen.filter(function (s) {
          return s.code === verwijzing.code && (!groep || s.groep === groep) &&
                 (!verwijzing.collectie || s.collectie === verwijzing.collectie);
        });
        if (kandidaten.length === 1) return { stof: kandidaten[0], reden: 'code-eenduidig' };
        if (kandidaten.length > 1) return { stof: null, reden: 'code-meervoudig', kandidaten: kandidaten };
      }
      return { stof: null, reden: 'niet-gevonden' };
    };

    /* --- staat ----------------------------------------------------- */
    var staat = { reeks: [], groep: '', stof: '', stap: 0 };

    /* Abonnees van de brug-API (zie onderaan start): worden na elke werkAlles
       aangeroepen, zodat een tweede weergavelaag — de 3D-viewer — meeloopt met
       elke wijziging, ook de asynchrone (nagelaadde varianten, stoffen-JSON). */
    var abonnees = [];
    var geschiedenis = [];
    var favorieten = {};
    try {
      favorieten = JSON.parse(localStorage.getItem('lf-bank-favorieten') || '{}') || {};
    } catch (e) { favorieten = {}; }

    var bewaarGeschiedenis = function () {
      geschiedenis.push(JSON.parse(JSON.stringify({ reeks: staat.reeks, groep: staat.groep, stof: staat.stof })));
      if (geschiedenis.length > 40) geschiedenis.shift();
    };

    /* --- meldingen -------------------------------------------------- */
    var meldingEl = el('melding');
    var tekstTimer = null;
    var verbergTimer = null;

    /* Alle terugkoppeling aan de klant loopt via dit ene element. Het is een
       live-gebied, zodat een schermlezer meldingen voorleest zonder dat de focus
       verspringt. Een fout wordt onderbrekend gemeld, de rest afwachtend.

       De tekst wordt bewust een tel later gezet dan het tonen: verschijnt een
       live-gebied en verandert de inhoud in dezelfde stap, dan slaan schermlezers
       de melding vaak over. */
    var meld = function (tekst, soort) {
      if (!meldingEl) return;
      clearTimeout(tekstTimer);
      clearTimeout(verbergTimer);
      if (!tekst) { meldingEl.hidden = true; meldingEl.textContent = ''; return; }
      meldingEl.setAttribute('data-soort', soort || 'let');
      meldingEl.setAttribute('aria-live', soort === 'fout' ? 'assertive' : 'polite');
      meldingEl.textContent = '';
      meldingEl.hidden = false;
      tekstTimer = setTimeout(function () { meldingEl.textContent = tekst; }, 50);
      if (soort === 'ok') {
        verbergTimer = setTimeout(function () { meldingEl.hidden = true; meldingEl.textContent = ''; }, 6000);
      }
    };

    /* ------------------------------------------------------------------ *
     * Plaatsingsregels
     * ------------------------------------------------------------------ */

    var isVrijstaand = function (element) { return element && element.plaatsing === 'vrijstaand'; };

    var reeksElementen = function () {
      return staat.reeks.map(function (code) { return elementenPerCode[code]; }).filter(Boolean);
    };
    var loopElementen = function () {
      return reeksElementen().filter(function (e) { return !isVrijstaand(e); });
    };

    /* De reeks bevat ook vrijstaande stukken (poef, hocker). Die tellen niet mee in
       de rij. Een plek die in rij-telling is uitgerekend moet daarom worden vertaald
       naar een plek in staat.reeks, anders komt het element ergens anders terecht
       dan bedoeld. */
    var loopIndexNaarReeks = function (loopIndex) {
      var geteld = 0;
      for (var i = 0; i < staat.reeks.length; i++) {
        if (isVrijstaand(elementenPerCode[staat.reeks[i]])) continue;
        if (geteld === loopIndex) return i;
        geteld++;
      }
      return staat.reeks.length;
    };

    /* Bepaalt op welke plek een element mag komen; geeft -1 bij een conflict. */
    var bepaalPositie = function (element) {
      var loop = staat.reeks.filter(function (c) { return !isVrijstaand(elementenPerCode[c]); });
      if (isVrijstaand(element)) return staat.reeks.length;

      switch (element.plaatsing) {
        case 'alleen-links':
          if (loop.some(function (c) { return elementenPerCode[c].plaatsing === 'alleen-links'; })) return -1;
          return loopIndexNaarReeks(0);
        case 'alleen-rechts':
          if (loop.some(function (c) { return elementenPerCode[c].plaatsing === 'alleen-rechts'; })) return -1;
          return staat.reeks.length;
        case 'alleen-uiteinde':
          /* Een uiteinde-element kan alleen aan het begin of het eind. Staan beide
             uiteinden al bezet, dan is er geen plek meer. Zonder deze controle werd
             een derde uiteinde-element wél toegevoegd en viel de bestelknop daarna
             grijs uit zonder uitleg. */
          if (!loop.length) return 0;
          if (loop.length >= 2) {
            var eersteVrij = elementenPerCode[loop[0]].plaatsing !== 'alleen-uiteinde' &&
                             elementenPerCode[loop[0]].plaatsing !== 'alleen-links';
            var laatsteVrij = elementenPerCode[loop[loop.length - 1]].plaatsing !== 'alleen-uiteinde' &&
                              elementenPerCode[loop[loop.length - 1]].plaatsing !== 'alleen-rechts';
            if (!eersteVrij && !laatsteVrij) return -1;
            if (!laatsteVrij) return loopIndexNaarReeks(0);
          }
          return staat.reeks.length;
        case 'alleen-midden':
          /* Op één na laatste plek in de rij, uitgedrukt in rij-telling. */
          if (loop.length < 2) return -1;
          return loopIndexNaarReeks(loop.length - 1);
        default:
          /* Vrij plaatsbaar: achteraan, maar vóór alles wat aan het eind hoort. */
          var grens = staat.reeks.length;
          for (var i = staat.reeks.length - 1; i >= 0; i--) {
            var achteraan = elementenPerCode[staat.reeks[i]];
            if (!achteraan) break;
            if (isVrijstaand(achteraan)) { grens = i; continue; }
            if (achteraan.plaatsing === 'alleen-rechts') { grens = i; continue; }
            if (achteraan.plaatsing === 'alleen-uiteinde' && loop.length > 1) { grens = i; continue; }
            break;
          }
          return grens;
      }
    };

    var conflictTekst = function (element) {
      switch (element.plaatsing) {
        case 'alleen-links':
          return 'Er staat al een element met een linkerarm in de opstelling. Een tweede linkerarm kan er niet naast passen.';
        case 'alleen-rechts':
          return 'Er staat al een element met een rechterarm in de opstelling. Een tweede rechterarm kan er niet naast passen.';
        case 'alleen-uiteinde':
          return element.naam + ' kan alleen aan het begin of het einde van de opstelling staan, en beide uiteinden zijn al bezet. ' +
                 'Haal eerst een eindelement weg, of kies een element dat tussenin past.';
        case 'alleen-midden':
          return 'Dit is een tussenelement. Kies eerst twee elementen waar het tussen kan staan.';
        default:
          return 'Dit element past niet op deze plek in de opstelling.';
      }
    };

    /* Controleert een reeks en geeft de eerste fout terug. Zonder argument wordt de
       huidige reeks gecontroleerd; met argument een voorgenomen reeks, zodat een
       ongeldige toevoeging kan worden geweigerd vóórdat hij wordt uitgevoerd. */
    var controleerReeks = function (reeks) {
      var bron = reeks || staat.reeks;
      var loop = bron.filter(function (c) { return !isVrijstaand(elementenPerCode[c]); });
      for (var i = 0; i < loop.length; i++) {
        var e = elementenPerCode[loop[i]];
        if (!e) continue;
        if (e.plaatsing === 'alleen-links' && i !== 0) {
          return e.naam + ' hoort helemaal links in de opstelling te staan.';
        }
        if (e.plaatsing === 'alleen-rechts' && i !== loop.length - 1) {
          return e.naam + ' hoort helemaal rechts in de opstelling te staan.';
        }
        if (e.plaatsing === 'alleen-uiteinde' && i !== 0 && i !== loop.length - 1) {
          return e.naam + ' kan alleen aan het begin of het einde van de opstelling staan.';
        }
        if (e.plaatsing === 'alleen-midden' && (i === 0 || i === loop.length - 1)) {
          return e.naam + ' is een tussenelement en kan niet aan het uiteinde staan.';
        }
      }
      return '';
    };

    /* ------------------------------------------------------------------ *
     * Prijs — altijd uit de varianten
     * ------------------------------------------------------------------ */

    var regels = function () {
      var gegroepeerd = [];
      var index = {};
      staat.reeks.forEach(function (code, positie) {
        var element = elementenPerCode[code];
        if (!element) return;
        if (index[code] == null) {
          index[code] = gegroepeerd.length;
          gegroepeerd.push({ element: element, aantal: 0, posities: [] });
        }
        gegroepeerd[index[code]].aantal += 1;
        gegroepeerd[index[code]].posities.push(positie + 1);
      });
      return gegroepeerd.map(function (regel) {
        var variant = variantVoor(regel.element, staat.groep);
        regel.variant = variant;
        /* Een variantprijs van nul is geen prijs maar een invoerfout: een nieuwe
           variant, een mislukte import of een lege cel in een prijsronde. Zonder
           deze controle telt hij als geldige nulregel mee, blijft de bestelknop
           groen en rekent Shopify de bestelling serverside af op nul euro. */
        regel.prijs = geldigePrijs(variant) ? Number(variant.prijs) : null;
        regel.totaal = regel.prijs == null ? null : regel.prijs * regel.aantal;
        return regel;
      });
    };

    /* Eén centrale regel voor het totaal, en die is streng:
       alle gekozen elementen hebben een geldige variant met een prijs boven nul
       EN de variantlijst is volledig geladen  ->  totaal berekenen.
       In elk ander geval is er geen totaal, en toont het scherm een streepje.

       Een deeltotaal van alleen de elementen die toevallig wel geladen zijn mag
       nooit verschijnen: de klant leest dat als de volledige prijs. */
    var totaalPrijs = function () {
      if (variantStatus !== 'compleet') return null;
      /* Zolang de stof niet eenduidig is, is de samenstelling niet compleet en
         hoort er geen bedrag te staan dat als eindprijs gelezen kan worden. */
      if (stofOnduidelijk || stofNogTeHerstellen) return null;
      var lijst = regels();
      if (!staat.groep || !lijst.length) return null;
      var som = 0;
      for (var i = 0; i < lijst.length; i++) {
        if (lijst[i].prijs == null) return null;
        som += lijst[i].totaal;
      }
      return som;
    };

    var totaleBreedte = function () {
      var loop = loopElementen();
      if (!loop.length) return null;
      var compleet = loop.every(function (e) { return getal(e.b) > 0 && getal(e.d) > 0; });
      if (!compleet) return null;
      var opstelling = bouwOpstelling(loop);
      return Math.round(opstelling.maxX - opstelling.minX);
    };

    /* Bij een hoekopstelling is één maat niet genoeg: dan hoort er ook een diepte bij. */
    var maatTekst = function () {
      var loop = loopElementen();
      if (!loop.length) return null;
      if (!loop.every(function (e) { return getal(e.b) > 0 && getal(e.d) > 0; })) return null;
      var opstelling = bouwOpstelling(loop);
      var b = Math.round(opstelling.maxX - opstelling.minX);
      var d = Math.round(opstelling.maxY - opstelling.minY);
      return loop.some(isHoek) ? b + ' × ' + d + ' cm' : b + ' cm';
    };

    /* Vanafprijs per element in de gekozen (of goedkoopste) stofgroep. */
    var elementPrijs = function (element) {
      if (staat.groep) {
        var v = variantVoor(element, staat.groep);
        return geldigePrijs(v) ? { prijs: Number(v.prijs), vanaf: false, beschikbaar: v.beschikbaar } : null;
      }
      var laagste = null;
      data.stofgroepen.forEach(function (g) {
        var v = variantVoor(element, g.code);
        if (geldigePrijs(v) && (laagste == null || Number(v.prijs) < laagste)) laagste = Number(v.prijs);
      });
      return laagste == null ? null : { prijs: laagste, vanaf: true, beschikbaar: true };
    };

    /* ------------------------------------------------------------------ *
     * Stofpatroon voor de tekening
     * ------------------------------------------------------------------ */

    /* staat.stof bevat de blijvende sleutel, niet de kleurcode. */
    var huidigeStof = function () {
      if (!staat.stof) return null;
      return stoffenPerSleutel[staat.stof] || null;
    };

    var patroonTeller = 0;

    /* Levert bij elkaar horende defs én vullingen voor één tekening.
       Elke tekening krijgt eigen pattern-id's. Eerder deelden alle tekeningen op de
       pagina drie vaste id's; dat werkt zolang alles dezelfde stof heeft en breekt
       zodra er twee stoffen tegelijk in beeld staan.

       Vullingen voor zitting, rug en armleuning: de rug en de armleuningen krijgen
       dezelfde stof met iets minder licht, zodat de vorm leesbaar blijft zonder dat
       de structuur van de stof verdwijnt. */
    var stofset = function () {
      var stof = huidigeStof();
      if (!stof || !stof.foto) {
        return { defs: '', vullingen: { zit: VLAK, rug: RUGVLAK, arm: RUGVLAK } };
      }
      var id = 'lf-stof-' + (++patroonTeller);
      /* Een tegel van 18 cm komt ongeveer overeen met het stuk stof dat op een
         staalfoto staat. Let op: dit is een schatting op het oog en geen gemeten
         rapportmaat. Zolang de werkelijke maat niet bij de leverancier is
         opgevraagd, staat de weefselschaal op de tekening niet vast. */
      var t = 18;
      var tegel = function (naam, kleur, dekking) {
        return '<pattern id="' + naam + '" patternUnits="userSpaceOnUse" width="' + t +
          '" height="' + t + '">' +
          '<image href="' + esc(stof.foto) + '" x="0" y="0" width="' + t + '" height="' + t +
          '" preserveAspectRatio="xMidYMid slice"/>' +
          (kleur ? '<rect width="' + t + '" height="' + t + '" fill="' + kleur +
                   '" opacity="' + dekking + '"/>' : '') +
          '</pattern>';
      };
      return {
        defs: '<defs>' + tegel(id) + tegel(id + '-rug', DIEP, 0.13) + tegel(id + '-arm', DIEP, 0.06) + '</defs>',
        vullingen: { zit: 'url(#' + id + ')', rug: 'url(#' + id + '-rug)', arm: 'url(#' + id + '-arm)' }
      };
    };

    /* ------------------------------------------------------------------ *
     * Tekenen van de opstelling
     * ------------------------------------------------------------------ */

    var tekenOpstelling = function () {
      var vlak = el('tekening');
      if (!vlak) return;
      var loop = loopElementen();
      var los = reeksElementen().filter(isVrijstaand);

      if (!loop.length && !los.length) {
        vlak.innerHTML = '<p class="lf-bank-tekening__leeg">Je opstelling verschijnt hier zodra je het eerste element kiest. ' +
                         'Begin met een voorbeeldopstelling of stel zelf samen.</p>';
        return;
      }
      var set = stofset();

      var zonderMaat = loop.filter(function (e) { return !getal(e.b) || !getal(e.d); });
      var opstelling = bouwOpstelling(loop);
      var breedteCm = Math.max(1, opstelling.maxX - opstelling.minX);
      var diepteCm = Math.max(1, opstelling.maxY - opstelling.minY);

      var losBreedte = los.reduce(function (som, e) { return som + (getal(e.b) || 70) + 14; }, 0);
      var totBreedte = Math.max(breedteCm, losBreedte);
      var losDiepte = los.length ? Math.max.apply(null, los.map(function (e) { return getal(e.d) || 70; })) + 24 : 0;

      var marge = 14;
      var svg = ['<svg viewBox="' + (opstelling.minX - marge) + ' ' + (opstelling.minY - marge) + ' ' +
                 (totBreedte + marge * 2) + ' ' + (diepteCm + losDiepte + marge * 2) +
                 '" role="img" aria-label="Bovenaanzicht van de samengestelde opstelling">'];
      svg.push(set.defs);

      opstelling.geplaatst.forEach(function (plek) {
        if (plek.zonderMaat) return;
        var t = tekenElement(plek.element, set.vullingen);
        svg.push('<g transform="translate(' + plek.x.toFixed(1) + ',' + plek.y.toFixed(1) + ') ' +
                 (plek.hoek ? 'rotate(' + plek.hoek + ' 0 0) ' : '') + '">' + t.onderdelen.join('') + '</g>');
      });

      /* Losse stukken (poef, hocker) apart onder de opstelling. */
      var lx = opstelling.minX;
      var ly = opstelling.maxY + 24;
      los.forEach(function (element) {
        var t = tekenElement(element, set.vullingen);
        svg.push('<g transform="translate(' + lx.toFixed(1) + ',' + ly.toFixed(1) + ')">' + t.onderdelen.join('') + '</g>');
        lx += t.b + 14;
      });

      /* Maatlijn onder de looprichting. */
      if (!zonderMaat.length && loop.length) {
        var my = opstelling.maxY + 9;
        svg.push('<line x1="' + opstelling.minX + '" y1="' + my + '" x2="' + opstelling.maxX + '" y2="' + my +
                 '" stroke="' + LIJN + '" stroke-width="0.7" opacity="0.55"/>');
        svg.push('<line x1="' + opstelling.minX + '" y1="' + (my - 4) + '" x2="' + opstelling.minX + '" y2="' + (my + 4) +
                 '" stroke="' + LIJN + '" stroke-width="0.7" opacity="0.55"/>');
        svg.push('<line x1="' + opstelling.maxX + '" y1="' + (my - 4) + '" x2="' + opstelling.maxX + '" y2="' + (my + 4) +
                 '" stroke="' + LIJN + '" stroke-width="0.7" opacity="0.55"/>');
        svg.push('<text x="' + ((opstelling.minX + opstelling.maxX) / 2) + '" y="' + (my - 5) +
                 '" text-anchor="middle" font-size="9" fill="' + LIJN + '" opacity="0.75" ' +
                 'font-family="Gotu, sans-serif">' + Math.round(breedteCm) + ' cm</text>');
      }

      svg.push('</svg>');
      vlak.innerHTML = svg.join('');

      var waarschuwing = el('tekening-waarschuwing');
      if (waarschuwing) {
        if (zonderMaat.length) {
          waarschuwing.hidden = false;
          waarschuwing.textContent = 'Van ' + zonderMaat.length + ' gekozen element' +
            (zonderMaat.length === 1 ? '' : 'en') + ' zijn de afmetingen nog niet aangeleverd. ' +
            'Die staan niet in de tekening en tellen niet mee in de totale breedte.';
        } else {
          waarschuwing.hidden = true;
        }
      }
    };

    /* ------------------------------------------------------------------ *
     * Volgorde-strip: verplaatsen en verwijderen
     * ------------------------------------------------------------------ */

    var tekenVolgorde = function () {
      var houder = el('volgorde');
      if (!houder) return;
      houder.innerHTML = '';
      if (!staat.reeks.length) return;

      staat.reeks.forEach(function (code, index) {
        var element = elementenPerCode[code];
        if (!element) return;
        var kaart = maak('div', 'lf-bank-volgorde__item');
        var set = stofset();
        kaart.innerHTML = elementSvg(element, set.vullingen, null, set.defs);
        var naam = maak('span', 'lf-bank-volgorde__naam', element.naam);
        kaart.appendChild(naam);

        var knoppen = maak('div', 'lf-bank-volgorde__knoppen');
        var links = maak('button', '', '←');
        links.type = 'button';
        links.setAttribute('aria-label', element.naam + ' naar links verplaatsen');
        links.disabled = index === 0;
        links.addEventListener('click', function () { verplaats(index, -1); });

        var rechts = maak('button', '', '→');
        rechts.type = 'button';
        rechts.setAttribute('aria-label', element.naam + ' naar rechts verplaatsen');
        rechts.disabled = index === staat.reeks.length - 1;
        rechts.addEventListener('click', function () { verplaats(index, 1); });

        var weg = maak('button', '', '✕');
        weg.type = 'button';
        weg.setAttribute('aria-label', element.naam + ' verwijderen');
        weg.addEventListener('click', function () { verwijder(index); });

        knoppen.appendChild(links);
        knoppen.appendChild(rechts);
        knoppen.appendChild(weg);
        kaart.appendChild(knoppen);
        houder.appendChild(kaart);
      });
    };

    var verplaats = function (index, richting) {
      var doel = index + richting;
      if (doel < 0 || doel >= staat.reeks.length) return;
      bewaarGeschiedenis();
      var kopie = staat.reeks.slice();
      var tijdelijk = kopie[index];
      kopie[index] = kopie[doel];
      kopie[doel] = tijdelijk;
      staat.reeks = kopie;
      var fout = controleerReeks();
      meld(fout, fout ? 'let' : '');
      werkAlles();
    };

    var verwijder = function (index) {
      bewaarGeschiedenis();
      staat.reeks.splice(index, 1);
      meld(controleerReeks(), 'let');
      werkAlles();
    };

    /* ------------------------------------------------------------------ *
     * Elementen toevoegen
     * ------------------------------------------------------------------ */

    var voegToe = function (element) {
      /* Eerst de opstellingsregel: die verklaart het beste waarom iets niet kan. */
      var positie = bepaalPositie(element);
      if (positie === -1) { meld(conflictTekst(element), 'let'); return; }

      /* De voorgenomen reeks wordt gecontroleerd vóór hij wordt uitgevoerd. Zo kan
         een toevoeging die de opstelling ongeldig zou maken worden geweigerd mét
         uitleg, in plaats van te worden geaccepteerd waarna de bestelknop grijs
         uitvalt zonder dat de klant weet waarom. */
      var voorgenomen = staat.reeks.slice();
      voorgenomen.splice(positie, 0, element.code);
      var reeksFout = controleerReeks(voorgenomen);
      if (reeksFout) { meld(reeksFout, 'let'); return; }

      if (staat.groep) {
        var variant = variantVoor(element, staat.groep);
        if (!variant) {
          meld('Dit element is niet leverbaar in de gekozen stofgroep. Kies een andere stofgroep of een ander element.', 'let');
          return;
        }
        if (!geldigePrijs(variant)) {
          meld('Voor dit element is in deze stofgroep nog geen prijs vastgesteld. Neem contact met ons op, dan zoeken wij het na.', 'let');
          return;
        }
        /* Niet-verkoopbare varianten blokkeren het samenstellen niet; bestellen wel. */
        if (!variant.beschikbaar && !allesOnbeschikbaar) {
          meld(element.naam + ' is in deze stofgroep momenteel niet beschikbaar.', 'let');
          return;
        }
      }
      bewaarGeschiedenis();
      staat.reeks.splice(positie, 0, element.code);
      meld(element.naam + ' toegevoegd.', 'ok');
      werkAlles();
    };

    var tekenElementen = function () {
      var houder = el('elementen');
      if (!houder) return;
      var actieveGroep = houder.dataset.groep || '';
      houder.innerHTML = '';

      var lijst = data.elementen.filter(function (e) {
        return !actieveGroep || (e.categorie || 'Overig') === actieveGroep;
      });

      lijst.forEach(function (element) {
        var kaart = maak('div', 'lf-bank-element');
        var aantal = staat.reeks.filter(function (c) { return c === element.code; }).length;
        if (aantal) kaart.dataset.gekozen = 'true';

        var beeld = maak('div', 'lf-bank-element__beeld');
        var set = stofset();
        beeld.innerHTML = elementSvg(element, set.vullingen, null, set.defs);
        kaart.appendChild(beeld);

        var naam = maak('div', 'lf-bank-element__naam');
        naam.textContent = element.naam;
        if (element.orientatie === 'links' || element.orientatie === 'rechts') {
          var zijde = maak('span', 'lf-bank-element__zijde', element.orientatie === 'links' ? 'L' : 'R');
          zijde.title = element.orientatie === 'links' ? 'Linkerzijde' : 'Rechterzijde';
          naam.appendChild(zijde);
        }
        kaart.appendChild(naam);

        var maten = [];
        if (getal(element.b)) maten.push(Math.round(getal(element.b)) + ' b');
        if (getal(element.d)) maten.push(Math.round(getal(element.d)) + ' d');
        if (getal(element.h)) maten.push(Math.round(getal(element.h)) + ' h');
        var meta = maak('div', 'lf-bank-element__meta');
        meta.textContent = element.code + (maten.length ? ' · ' + maten.join(' × ') + ' cm' : ' · afmetingen nog niet bekend');
        kaart.appendChild(meta);

        var prijsInfo = elementPrijs(element);
        var prijs = maak('div', 'lf-bank-element__prijs');
        if (prijsInfo == null) {
          prijs.dataset.ontbreekt = 'true';
          /* Onderscheid maken tussen "bestaat niet", "nog aan het laden" en
             "kon niet worden geladen". Alle drie zagen er eerder hetzelfde uit,
             en de tekst "nog niet ingevoerd" was in twee ervan onwaar. */
          prijs.textContent = variantStatus === 'laden'
            ? 'Prijs wordt opgehaald…'
            : (variantStatus === 'onvolledig'
                ? 'Prijs kon niet worden geladen'
                : 'Prijs nog niet in Shopify ingevoerd');
          kaart.dataset.uit = 'true';
        } else {
          prijs.textContent = (prijsInfo.vanaf ? 'vanaf ' : '') + geld(prijsInfo.prijs);
        }
        kaart.appendChild(prijs);

        var acties = maak('div', 'lf-bank-element__toevoegen');
        if (aantal) {
          var min = maak('button', '', '−');
          min.type = 'button';
          min.setAttribute('aria-label', 'Eén ' + element.naam + ' verwijderen');
          min.addEventListener('click', function () {
            var laatste = staat.reeks.lastIndexOf(element.code);
            if (laatste > -1) verwijder(laatste);
          });
          acties.appendChild(min);
          var teller = maak('span', 'lf-bank-element__aantal', String(aantal));
          teller.setAttribute('aria-label', aantal + ' keer gekozen');
          acties.appendChild(teller);
        }
        var plus = maak('button', 'lf-bank-element__plus', aantal ? 'Nog een' : 'Toevoegen');
        plus.type = 'button';
        plus.disabled = prijsInfo == null;
        plus.addEventListener('click', function () { voegToe(element); });
        acties.appendChild(plus);
        kaart.appendChild(acties);

        houder.appendChild(kaart);
      });
    };

    var tekenElementgroepen = function () {
      var houder = el('elementgroepen');
      if (!houder) return;
      var categorieen = [];
      data.elementen.forEach(function (e) {
        var c = e.categorie || 'Overig';
        if (categorieen.indexOf(c) === -1) categorieen.push(c);
      });
      houder.innerHTML = '';
      var doelHouder = el('elementen');

      var maakKnop = function (label, waarde) {
        var knop = maak('button', 'lf-bank-groep', label);
        knop.type = 'button';
        knop.setAttribute('aria-pressed', (doelHouder.dataset.groep || '') === waarde ? 'true' : 'false');
        knop.addEventListener('click', function () {
          doelHouder.dataset.groep = waarde;
          tekenElementgroepen();
          tekenElementen();
        });
        houder.appendChild(knop);
      };
      maakKnop('Alle elementen', '');
      categorieen.forEach(function (c) { maakKnop(c, c); });
    };

    /* ------------------------------------------------------------------ *
     * Voorbeeldopstellingen
     * ------------------------------------------------------------------ */

    var tekenPresets = function () {
      var houder = el('presets');
      if (!houder) return;
      houder.innerHTML = '';
      if (!data.opstellingen || !data.opstellingen.length) {
        houder.closest('[data-lf-presets-blok]') &&
          (houder.closest('[data-lf-presets-blok]').hidden = true);
        return;
      }

      data.opstellingen.forEach(function (opstelling) {
        var elementen = naarLijst(opstelling.codes)
          .map(function (c) { return elementenPerCode[String(c).trim()]; })
          .filter(Boolean);
        if (!elementen.length) return;

        var kaart = maak('button', 'lf-bank-preset');
        kaart.type = 'button';

        var beeld = maak('div', 'lf-bank-preset__beeld');
        if (opstelling.afbeelding) {
          beeld.innerHTML = '<img src="' + esc(opstelling.afbeelding) + '" alt="' + esc(opstelling.naam) +
                            '" loading="lazy" decoding="async">';
        } else {
          var plan = bouwOpstelling(elementen.filter(function (e) { return !isVrijstaand(e); }));
          var marge = 10;
          var breed = Math.max(1, plan.maxX - plan.minX);
          var diep = Math.max(1, plan.maxY - plan.minY);
          var svg = ['<svg viewBox="' + (plan.minX - marge) + ' ' + (plan.minY - marge) + ' ' +
                     (breed + marge * 2) + ' ' + (diep + marge * 2) + '" role="img" aria-label="' +
                     esc(opstelling.naam) + ', bovenaanzicht">'];
          plan.geplaatst.forEach(function (plek) {
            if (plek.zonderMaat) return;
            var t = tekenElement(plek.element, { zit: VLAK, rug: RUGVLAK, arm: RUGVLAK });
            svg.push('<g transform="translate(' + plek.x.toFixed(1) + ',' + plek.y.toFixed(1) + ') ' +
                     (plek.hoek ? 'rotate(' + plek.hoek + ' 0 0)' : '') + '">' + t.onderdelen.join('') + '</g>');
          });
          svg.push('</svg>');
          beeld.innerHTML = svg.join('');
        }
        kaart.appendChild(beeld);
        kaart.appendChild(maak('div', 'lf-bank-preset__naam', opstelling.naam));

        var breedte = null;
        var loop = elementen.filter(function (e) { return !isVrijstaand(e); });
        if (loop.length && loop.every(function (e) { return getal(e.b); })) {
          var p = bouwOpstelling(loop);
          breedte = Math.round(p.maxX - p.minX);
        }
        var meta = [elementen.length + ' element' + (elementen.length === 1 ? '' : 'en')];
        if (breedte) meta.push(breedte + ' cm breed');
        kaart.appendChild(maak('div', 'lf-bank-preset__meta',
          opstelling.omschrijving ? opstelling.omschrijving : meta.join(' · ')));

        kaart.addEventListener('click', function () {
          bewaarGeschiedenis();
          staat.reeks = elementen.map(function (e) { return e.code; });
          /* Een voorbeeldopstelling zet de hele reeks in één keer en gaat dus niet
             door voegToe heen. Zonder deze controle kan een opstelling die in
             Shopify verkeerd is ingevoerd de bestelknop grijs maken zonder dat de
             klant één woord uitleg krijgt. */
          var fout = controleerReeks();
          if (fout) {
            meld('Let op: de voorbeeldopstelling "' + opstelling.naam + '" klopt niet. ' + fout +
                 ' Pas de volgorde aan of meld dit bij ons.', 'fout');
          } else {
            meld('Opstelling "' + opstelling.naam + '" overgenomen. Je kunt hem nog aanpassen.', 'ok');
          }
          gaNaarStap(1);
          werkAlles();
        });
        houder.appendChild(kaart);
      });
    };

    /* ------------------------------------------------------------------ *
     * Stofgroepen en stoffen
     * ------------------------------------------------------------------ */

    var stoffilter = { zoek: '', collectie: '', kleur: '', alleenFavoriet: false };

    var tekenStofgroepen = function () {
      var houder = el('stofgroepen');
      if (!houder) return;
      houder.innerHTML = '';
      data.stofgroepen.forEach(function (groep) {
        var knop = maak('button', 'lf-bank-stofgroep');
        knop.type = 'button';
        knop.setAttribute('aria-pressed', staat.groep === groep.code ? 'true' : 'false');
        knop.appendChild(document.createTextNode(groep.label));

        var aantal = data.stoffen.filter(function (s) { return s.groep === groep.code; }).length;
        var sub = maak('small', '', aantal ? aantal + ' stoffen' : 'stoffen nog niet ingevoerd');
        knop.appendChild(sub);

        if (geblokkeerdeGroepen[groep.code]) {
          knop.disabled = true;
          knop.setAttribute('data-geblokkeerd', 'true');
          sub.textContent = 'tijdelijk niet te kiezen';
        }

        knop.addEventListener('click', function () {
          if (geblokkeerdeGroepen[groep.code]) {
            meld('De kleuren van ' + groep.label + ' konden niet volledig worden geladen. ' +
                 'Deze stofgroep is daarom tijdelijk niet te kiezen. Ververs de pagina of neem contact met ons op.', 'fout');
            return;
          }
          bewaarGeschiedenis();
          staat.groep = groep.code;
          /* Stof buiten de nieuwe groep laten vallen. */
          var vorige = huidigeStof();
          if (vorige && vorige.groep !== groep.code) staat.stof = '';
          var missend = loopElementen().concat(reeksElementen().filter(isVrijstaand))
            .filter(function (e) { return !variantVoor(e, groep.code); });
          if (missend.length) {
            meld('Let op: ' + missend.length + ' gekozen element' + (missend.length === 1 ? '' : 'en') +
                 ' heeft geen prijs in ' + groep.label + '. Bestellen is geblokkeerd tot dat is opgelost.', 'fout');
          } else {
            meld('');
          }
          werkAlles();
        });
        houder.appendChild(knop);
      });
    };

    var zichtbareStoffen = function () {
      return data.stoffen.filter(function (stof) {
        if (staat.groep && stof.groep !== staat.groep) return false;
        if (stoffilter.collectie && stof.collectie !== stoffilter.collectie) return false;
        if (stoffilter.kleur && stof.kleurfamilie !== stoffilter.kleur) return false;
        if (stoffilter.alleenFavoriet && !favorieten[stof.groep + '|' + stof.code]) return false;
        if (stoffilter.zoek) {
          /* Elk woord apart, en alle woorden moeten voorkomen. Zo vindt "yarra 101"
             ook Yarra Bruin 101, waar zoeken op de hele term als een geheel
             niets oplevert. */
          var hooiberg = normaliseer([stof.naam, stof.code, stof.collectie,
                                      stof.kleurnaam, stof.kleurfamilie].join(' '));
          var woorden = normaliseer(stoffilter.zoek).split(' ').filter(Boolean);
          for (var wi = 0; wi < woorden.length; wi++) {
            if (hooiberg.indexOf(woorden[wi]) === -1) return false;
          }
        }
        return true;
      });
    };

    var tekenStoffen = function () {
      var houder = el('stoffen');
      if (!houder) return;
      houder.innerHTML = '';
      var leegEl = el('stoffen-leeg');

      if (!staat.groep) {
        if (leegEl) { leegEl.hidden = false; leegEl.textContent = 'Kies eerst een stofgroep. Daarna zie je alle stofkleuren die daarbij horen.'; }
        return;
      }
      var lijst = zichtbareStoffen();
      if (!lijst.length) {
        if (leegEl) {
          leegEl.hidden = false;
          leegEl.textContent = data.stoffen.filter(function (s) { return s.groep === staat.groep; }).length
            ? 'Geen stoffen gevonden. Wis je zoekterm of pas de filters aan.'
            : 'Voor deze stofgroep zijn nog geen stofstalen in Shopify ingevoerd. Neem contact op voor de actuele stoffenkaart.';
        }
        return;
      }
      if (leegEl) leegEl.hidden = true;

      lijst.forEach(function (stof) {
        /* De vergrootknop staat naast de keuzeknop, niet erin. Een knop in een knop
           is ongeldige HTML, gedraagt zich per browser anders en was met het
           toetsenbord niet te bereiken. */
        var kaart = maak('div', 'lf-bank-stof-kaart');
        var knop = maak('button', 'lf-bank-stof');
        knop.type = 'button';
        knop.setAttribute('aria-pressed', staat.stof === (stof.configurator_key || stof.handle || stof.code) ? 'true' : 'false');

        var staal = maak('div', 'lf-bank-stof__staal');
        if (stof.foto) {
          staal.innerHTML = '<img src="' + esc(stof.foto) + '" alt="Stofstaal ' + esc(stof.naam) +
                            '" loading="lazy" decoding="async" width="300" height="300">';
        } else {
          staal.innerHTML = '<span class="lf-bank-stof__geen">Foto stofstaal ontbreekt</span>';
        }
        staal.insertAdjacentHTML('beforeend',
          '<span class="lf-bank-stof__vink" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" ' +
          'stroke-linecap="round" stroke-linejoin="round"><path d="m5 13 4 4L19 7"/></svg></span>');
        knop.appendChild(staal);

        knop.appendChild(maak('span', 'lf-bank-stof__naam', stof.naam));
        knop.appendChild(maak('span', 'lf-bank-stof__code',
          [stof.code, stof.collectie].filter(Boolean).join(' · ')));

        knop.addEventListener('click', function () {
          bewaarGeschiedenis();
          staat.stof = stof.configurator_key || stof.handle || stof.code;
          stofOnduidelijk = null;
          stofNogTeHerstellen = null;
          meld('');
          werkAlles();
        });
        var loep = maak('button', 'lf-bank-stof__loep');
        loep.type = 'button';
        loep.innerHTML = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="#4A3C31" ' +
                         'stroke-width="1.6" aria-hidden="true"><circle cx="11" cy="11" r="6.5"/><path d="m20 20-3.6-3.6"/></svg>';
        loep.setAttribute('aria-label', 'Bekijk ' + stof.naam + ' in het groot');
        loep.addEventListener('click', function () { toonStofdetail(stof); });

        kaart.appendChild(knop);
        kaart.appendChild(loep);
        houder.appendChild(kaart);
      });
    };

    var tekenStoffilters = function () {
      var collectieHouder = el('filter-collectie');
      var kleurHouder = el('filter-kleur');
      var samenvatting = el('filter-samenvatting');

      var basis = data.stoffen.filter(function (s) { return !staat.groep || s.groep === staat.groep; });
      var uniek = function (sleutel) {
        var lijst = [];
        basis.forEach(function (s) {
          if (s[sleutel] && lijst.indexOf(s[sleutel]) === -1) lijst.push(s[sleutel]);
        });
        return lijst.sort();
      };

      var vul = function (houder, waarden, veld) {
        if (!houder) return;
        houder.innerHTML = '';
        var maakKnop = function (label, waarde) {
          var knop = maak('button', '', label);
          knop.type = 'button';
          knop.setAttribute('aria-pressed', stoffilter[veld] === waarde ? 'true' : 'false');
          knop.addEventListener('click', function () {
            stoffilter[veld] = stoffilter[veld] === waarde ? '' : waarde;
            tekenStoffilters();
            tekenStoffen();
          });
          houder.appendChild(knop);
        };
        maakKnop('Alle', '');
        waarden.forEach(function (w) { maakKnop(w, w); });
      };

      vul(collectieHouder, uniek('collectie'), 'collectie');
      vul(kleurHouder, uniek('kleurfamilie'), 'kleur');

      if (samenvatting) {
        var totaal = basis.length;
        var getoond = zichtbareStoffen().length;
        samenvatting.textContent = getoond === totaal
          ? 'Alle ' + totaal + ' stoffen'
          : getoond + ' van ' + totaal + ' stoffen';
      }

      var favorietKnop = el('filter-favoriet');
      if (favorietKnop) favorietKnop.setAttribute('aria-pressed', stoffilter.alleenFavoriet ? 'true' : 'false');
    };

    /* Vergrote stofweergave. */
    var toonStofdetail = function (stof) {
      var laag = maak('div', 'lf-bank-stoflaag');
      laag.setAttribute('role', 'dialog');
      laag.setAttribute('aria-modal', 'true');
      laag.setAttribute('aria-label', 'Stofstaal ' + stof.naam);

      var rijen = [
        ['Stofnaam', stof.naam],
        ['Kleurcode', stof.code],
        ['Collectie', stof.collectie],
        ['Kleurfamilie', stof.kleurfamilie],
        ['Stofgroep', (data.stofgroepen.filter(function (g) { return g.code === stof.groep; })[0] || {}).label || stof.groep],
        ['Uitstraling', stof.uitstraling],
        ['Materiaal', stof.materiaal],
        ['Slijtvastheid', stof.martindale],
        ['Onderhoud', stof.onderhoud]
      ].filter(function (rij) { return rij[1]; });

      laag.innerHTML =
        '<div class="lf-bank-stoflaag__kaart" style="position:relative">' +
          '<div class="lf-bank-stoflaag__beeld">' +
            (stof.foto
              ? '<img src="' + esc(stof.foto) + '" alt="Stofstaal ' + esc(stof.naam) + ' op ware grootte">'
              : '<div class="lf-bank-stof__geen">Van deze stof is nog geen foto van het staal beschikbaar.</div>') +
          '</div>' +
          '<div class="lf-bank-stoflaag__info">' +
            '<p class="lf-bank__kicker">Stofstaal</p>' +
            '<h3 class="lf-bank__kop">' + esc(stof.naam) + '</h3>' +
            '<dl>' + rijen.map(function (rij) {
              return '<dt>' + esc(rij[0]) + '</dt><dd>' + esc(rij[1]) + '</dd>';
            }).join('') + '</dl>' +
            (stof.uitlopend ? '<p class="lf-bank-melding" data-soort="let">Deze stof loopt uit de collectie. Vraag de actuele beschikbaarheid na.</p>' : '') +
            '<p class="lf-bank-stoflaag__let">Kleuren op een scherm kunnen afwijken van de werkelijke stof, ' +
            'en de weefselstructuur in de tekening is een benadering: de werkelijke rapportmaat van deze stof ' +
            'is bij ons nog niet bevestigd. Vraag bij twijfel een stofstaal aan of bekijk de stof in de showroom.</p>' +
            '<div class="lf-bank-overzicht__acties">' +
              '<button type="button" class="lf-bank__knop" data-kies>Deze stof kiezen</button>' +
              '<button type="button" class="lf-bank__knop lf-bank__knop--rand" data-favoriet>' +
                (favorieten[stof.groep + '|' + stof.code] ? 'Uit favorieten' : 'Bewaar als favoriet') + '</button>' +
              (data.stofstaalLink ? '<a class="lf-bank__knop lf-bank__knop--rand" href="' + esc(data.stofstaalLink) + '">Stofstaal aanvragen</a>' : '') +
            '</div>' +
          '</div>' +
          '<button type="button" class="lf-bank-stoflaag__sluit" aria-label="Sluiten">' +
            '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#4A3C31" stroke-width="1.5" ' +
            'stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
        '</div>';

      var sluit = function () {
        document.removeEventListener('keydown', opToets);
        document.body.style.overflow = vorigeOverflow;
        laag.remove();
      };
      var opToets = function (gebeurtenis) { if (gebeurtenis.key === 'Escape') sluit(); };
      var vorigeOverflow = document.body.style.overflow;

      laag.querySelector('.lf-bank-stoflaag__sluit').addEventListener('click', sluit);
      laag.addEventListener('click', function (g) { if (g.target === laag) sluit(); });
      laag.querySelector('[data-kies]').addEventListener('click', function () {
        bewaarGeschiedenis();
        staat.groep = stof.groep;
        staat.stof = stof.configurator_key || stof.handle || stof.code;
        stofOnduidelijk = null;
        sluit();
        werkAlles();
      });
      laag.querySelector('[data-favoriet]').addEventListener('click', function () {
        var sleutel = stof.groep + '|' + stof.code;
        if (favorieten[sleutel]) delete favorieten[sleutel]; else favorieten[sleutel] = 1;
        try { localStorage.setItem('lf-bank-favorieten', JSON.stringify(favorieten)); } catch (e) {}
        this.textContent = favorieten[sleutel] ? 'Uit favorieten' : 'Bewaar als favoriet';
      });

      document.addEventListener('keydown', opToets);
      document.body.style.overflow = 'hidden';
      document.body.appendChild(laag);
      laag.querySelector('.lf-bank-stoflaag__sluit').focus();
    };

    /* ------------------------------------------------------------------ *
     * Overzicht, samenvatting en knoppen
     * ------------------------------------------------------------------ */

    var tekenOverzicht = function () {
      var houder = el('overzicht-regels');
      var leeg = el('overzicht-leeg');
      var lijst = regels();

      if (houder) {
        houder.innerHTML = '';
        lijst.forEach(function (regel) {
          var li = maak('li', 'lf-bank-overzicht__regel');
          var beeld = maak('div');
          var set = stofset();
          beeld.innerHTML = elementSvg(regel.element, set.vullingen, null, set.defs);
          li.appendChild(beeld);

          var midden = maak('div');
          midden.appendChild(document.createTextNode(regel.aantal + '× ' + regel.element.naam));
          var extra = maak('em', '', regel.element.code +
            (getal(regel.element.b) ? ' · ' + Math.round(getal(regel.element.b)) + ' cm breed' : ''));
          midden.appendChild(extra);
          li.appendChild(midden);

          var prijs = maak('div');
          if (!staat.groep) prijs.textContent = '—';
          else if (regel.prijs == null) { prijs.textContent = 'geen prijs'; prijs.style.color = '#8A5A2B'; }
          else prijs.textContent = geld(regel.totaal);
          li.appendChild(prijs);

          houder.appendChild(li);
        });
      }
      if (leeg) leeg.hidden = lijst.length > 0;

      /* Gekozen stof. */
      var stofHouder = el('overzicht-stof');
      var stof = huidigeStof();
      if (stofHouder) {
        if (!stof) {
          stofHouder.hidden = true;
        } else {
          stofHouder.hidden = false;
          var groepLabel = (data.stofgroepen.filter(function (g) { return g.code === stof.groep; })[0] || {}).label || stof.groep;
          stofHouder.innerHTML =
            (stof.foto ? '<img src="' + esc(stof.foto) + '" alt="Gekozen stof ' + esc(stof.naam) + '">' : '<span></span>') +
            '<span>' + esc(stof.naam) + '<em>' + esc([stof.code, groepLabel].filter(Boolean).join(' · ')) + '</em></span>';
        }
      }

      /* Totaal. */
      var totaal = totaalPrijs();
      var totaalEl = el('overzicht-totaal');
      var mobielTotaal = el('mobiel-totaal');
      var tekst = totaal == null ? '—' : geld(totaal);
      if (totaalEl) totaalEl.textContent = tekst;
      if (mobielTotaal) mobielTotaal.textContent = tekst;

      var maat = maatTekst();
      var breedteEl = el('overzicht-breedte');
      if (breedteEl) breedteEl.textContent = maat || '—';
      var tekeningMaat = el('tekening-maat');
      if (tekeningMaat) {
        tekeningMaat.innerHTML = maat
          ? (maat.indexOf('×') > -1 ? 'Buitenmaat <b>' : 'Totale breedte <b>') + maat + '</b>'
          : '';
      }

      /* Knopstatus. */
      var fout = controleerReeks();
      var kanBestellen = !!(staat.reeks.length && staat.groep && staat.stof && totaal != null &&
        !fout && !allesOnbeschikbaar && variantStatus === 'compleet' && !stofOnduidelijk);
      alle('bestel').forEach(function (knop) { knop.disabled = !kanBestellen; });

      var toestand = el('variantstatus');
      if (toestand) {
        if (variantStatus === 'laden') {
          toestand.hidden = false;
          toestand.setAttribute('data-soort', 'let');
          toestand.textContent = 'De prijzen van alle stofgroepen worden opgehaald. Een ogenblik.';
        } else if (variantStatus === 'onvolledig') {
          toestand.hidden = false;
          toestand.setAttribute('data-soort', 'fout');
          toestand.textContent = 'De prijzen konden niet volledig worden geladen, daarom is bestellen ' +
            'geblokkeerd. Ververs de pagina, of neem contact met ons op als dit blijft staan.';
        } else {
          toestand.hidden = true;
          toestand.textContent = '';
        }
      }

      var mobiel = el('mobiel');
      if (mobiel) mobiel.setAttribute('data-zichtbaar', staat.reeks.length ? 'true' : 'false');

      /* Samenvatting in stap 5. */
      var samenvatting = el('samenvatting');
      if (samenvatting) {
        if (!lijst.length) {
          samenvatting.innerHTML = '<p class="lf-bank__tekst">Zodra je elementen en een stof hebt gekozen, ' +
            'zie je hier je volledige samenstelling.</p>';
        } else {
          var rijen = [
            ['Merk', data.merk],
            ['Model', data.model],
            ['Elementen', lijst.map(function (r) { return r.aantal + '× ' + r.element.naam; }).join(', ')],
            ['Buitenmaat opstelling', maat || 'niet te bepalen — afmetingen ontbreken'],
            ['Stof', stof ? stof.naam + (stof.code ? ' – ' + stof.code : '') : 'nog niet gekozen'],
            ['Stofcollectie', stof ? (stof.collectie || '—') : '—'],
            ['Kleurnaam', stof ? (stof.kleurnaam || '—') : '—'],
            ['Stofgroep', staat.groep
              ? ((data.stofgroepen.filter(function (g) { return g.code === staat.groep; })[0] || {}).label || staat.groep)
              : 'nog niet gekozen'],
            ['Verwachte levertijd', data.levertijd || 'nog niet bevestigd'],
            ['Totaalprijs', totaal == null ? 'nog niet te berekenen' : geld(totaal)]
          ];
          samenvatting.innerHTML =
            '<table class="lf-bank-info__tabel"><tbody>' +
            rijen.map(function (rij) {
              return '<tr><th scope="row">' + esc(rij[0]) + '</th><td>' + esc(rij[1]) + '</td></tr>';
            }).join('') +
            '</tbody></table>';
        }
      }
    };

    /* ------------------------------------------------------------------ *
     * Stappen
     * ------------------------------------------------------------------ */

    var gaNaarStap = function (index, verplaatsFocus) {
      staat.stap = index;
      alle('stap').forEach(function (knop, i) {
        var actief = i === index;
        knop.setAttribute('aria-selected', actief ? 'true' : 'false');
        /* Roving tabindex: alleen de actieve stap zit in de tabvolgorde, de rest is
           met de pijltjestoetsen bereikbaar. Zo is de stappenbalk één stop in het
           tabpad in plaats van vijf. */
        knop.setAttribute('tabindex', actief ? '0' : '-1');
        if (actief && verplaatsFocus) knop.focus();
      });
      alle('paneel').forEach(function (paneel, i) {
        paneel.setAttribute('data-actief', i === index ? 'true' : 'false');
      });
    };

    var werkStapstatus = function () {
      var klaar = [
        staat.reeks.length > 0,
        staat.reeks.length > 0,
        !!staat.groep,
        !!staat.stof,
        !!(staat.reeks.length && staat.groep && staat.stof)
      ];
      alle('stap').forEach(function (knop, i) {
        knop.setAttribute('data-klaar', klaar[i] ? 'true' : 'false');
      });
    };

    /* ------------------------------------------------------------------ *
     * Delen, opslaan, herstellen
     * ------------------------------------------------------------------ */

    var configuratieCode = function () {
      var prefix = data.prefix || (data.model || 'BANK').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
      var basis = staat.reeks.join('-') + '|' + staat.groep + '|' + staat.stof;
      var som = 0;
      for (var i = 0; i < basis.length; i++) som = (som * 31 + basis.charCodeAt(i)) >>> 0;
      return prefix + '-' + som.toString(36).toUpperCase();
    };

    /* Versie van de deel-URL. Verandert de inhoud van de code, dan gaat dit getal
       omhoog en herkent een nieuwere configurator een oudere link als zodanig. */
    /* Versie 2: de stof wordt volledig geidentificeerd in plaats van met alleen
       een kleurcode. Versie 1 sloeg s op als kleurcode; die links worden nog
       gelezen, maar alleen wanneer ze eenduidig zijn. */
    var DEEL_VERSIE = 2;


    var stofVoorLink = function () {
      var stof = huidigeStof();
      if (!stof) return null;
      return {
        sleutel: stof.configurator_key || null,
        id: stof.id || null,
        handle: stof.handle || null,
        merk: stof.merk || data.merk || null,
        groep: stof.groep || null,
        collectie: stof.collectie || null,
        kleurnaam: stof.kleurnaam || null,
        code: stof.code || null
      };
    };

    var deelUrl = function () {
      var code = codeer({ v: DEEL_VERSIE, cv: VERSIE, r: staat.reeks, g: staat.groep, s: stofVoorLink() });
      if (!code) return window.location.href;
      return window.location.origin + window.location.pathname + '#cfg=' + code;
    };

    var herstel = function () {
      var uitHash = (window.location.hash.match(/cfg=([^&]+)/) || [])[1];
      var uitOpslag = null;
      try { uitOpslag = sessionStorage.getItem('lf-bank-' + data.productId); } catch (e) {}
      var bron = uitHash || uitOpslag;
      if (!bron) return;
      var waarde = decodeer(bron);
      if (!waarde || !Array.isArray(waarde.r)) return;

      if (waarde.v != null && waarde.v > DEEL_VERSIE) {
        if (uitHash) {
          meld('Deze gedeelde link komt uit een nieuwere versie van de configurator en kan hier niet ' +
               'volledig worden getoond. Stel de opstelling opnieuw samen of vraag een nieuwe link.', 'let');
        }
        return;
      }

      var gevraagd = waarde.r.length;
      staat.reeks = waarde.r.filter(function (c) { return elementenPerCode[c]; });
      var kwijt = gevraagd - staat.reeks.length;

      var groepGevraagd = !!waarde.g;
      var groepGevonden = groepGevraagd && data.stofgroepen.some(function (g) { return g.code === waarde.g; });
      if (groepGevonden) staat.groep = waarde.g;

      var stofGevraagd = !!waarde.s;
      var uitkomst = stofGevraagd ? zoekStof(waarde.s) : { stof: null, reden: 'leeg' };
      var stofGevonden = !!uitkomst.stof;
      if (stofGevonden) {
        staat.stof = uitkomst.stof.configurator_key || uitkomst.stof.handle;
        stofOnduidelijk = null;
      } else if (uitkomst.reden === 'niet-gevonden' || uitkomst.reden === 'leeg') {
        /* De gevraagde stof zit (nog) niet in de geladen lijst. Dat kan kloppen —
           hij bestaat niet meer — maar het kan ook zijn dat de stoffenbibliotheek
           nog onderweg is: het herstel draait vóór dat bestand binnen is. Bewaar
           de verwijzing en probeer het opnieuw zodra de bibliotheek er is. */
        staat.stof = '';
        if (stofGevraagd) stofNogTeHerstellen = waarde.s;
      } else if (uitkomst.reden === 'code-meervoudig') {
        /* Een oude link met alleen een kleurcode die meer dan een stof aanwijst.
           Hier mag nooit iets worden gekozen: dan zou de ontvanger een andere
           stof zien dan de afzender bedoelde. */
        staat.stof = '';
        stofOnduidelijk = {
          code: (typeof waarde.s === 'string' ? waarde.s : waarde.s.code),
          aantal: uitkomst.kandidaten.length,
          namen: uitkomst.kandidaten.map(function (k) { return k.naam; })
        };
        meldFout('stof-niet-eenduidig', 'herstel', {
          bankprogramma: data.model, stofgroep: staat.groep,
          toelichting: 'kleurcode ' + stofOnduidelijk.code + ' wijst ' +
            uitkomst.kandidaten.length + ' stoffen aan'
        });
      }

      if (!uitHash) return;

      /* Wat niet meer bestaat wordt weggelaten. Dat mag nooit stil gebeuren: de
         ontvanger zou anders een andere bank zien dan de afzender bedoelde. */
      var verschillen = [];
      if (kwijt) {
        verschillen.push(kwijt + ' element' + (kwijt === 1 ? '' : 'en') + ' uit de link ' +
          (kwijt === 1 ? 'wordt' : 'worden') + ' niet meer gevoerd');
      }
      if (groepGevraagd && !groepGevonden) verschillen.push('de stofgroep uit de link is hier niet leverbaar');
      if (stofOnduidelijk) {
        meld('De stof uit deze link is niet eenduidig: kleurcode ' + stofOnduidelijk.code +
             ' hoort bij ' + stofOnduidelijk.aantal + ' verschillende stoffen (' +
             stofOnduidelijk.namen.join(', ') + '). Kies zelf de juiste stof; ' +
             'bestellen is geblokkeerd tot dat is gebeurd.', 'let');
        return;
      }
      if (stofGevraagd && !stofGevonden) verschillen.push('de stofkleur uit de link is niet meer beschikbaar');

      if (verschillen.length) {
        meld('Let op: deze gedeelde samenstelling is niet compleet overgenomen — ' +
             verschillen.join(', ') + '. Controleer de opstelling voordat je bestelt.', 'let');
      } else {
        meld('Een gedeelde samenstelling is geladen. Je kunt hem gewoon aanpassen.', 'ok');
      }
    };

    var bewaar = function () {
      try {
        sessionStorage.setItem('lf-bank-' + data.productId,
          codeer({ v: DEEL_VERSIE, cv: VERSIE, r: staat.reeks, g: staat.groep, s: stofVoorLink() }));
      } catch (e) {}
    };

    /* ------------------------------------------------------------------ *
     * Winkelwagen
     * ------------------------------------------------------------------ */

    var naarWinkelwagen = function (knop) {
      var lijst = regels();
      var stof = huidigeStof();
      if (!lijst.length || !staat.groep || !stof) return;

      /* Eerst de toestand van de variantlijst. Staat die niet op compleet, dan is
         een ontbrekende variant geen bewijs dat hij niet bestaat — hij is alleen
         nog niet geladen. Die controle moet dus vóór de melding "bestaat geen
         variant" komen, anders krijgt de klant een verkeerde diagnose. */
      if (stofOnduidelijk) {
        meld('Kies eerst zelf de juiste stof. De gedeelde link gaf kleurcode ' +
             stofOnduidelijk.code + ', en die hoort bij meer dan een stof.', 'fout');
        return;
      }
      if (variantStatus !== 'compleet') {
        meld(variantStatus === 'laden'
          ? 'De prijzen worden nog opgehaald. Probeer het over een ogenblik opnieuw.'
          : 'De prijzen konden niet volledig worden geladen. Bestellen is geblokkeerd; ververs de pagina.', 'fout');
        return;
      }

      var ontbrekend = lijst.filter(function (r) { return !r.variant; });
      if (ontbrekend.length) {
        ontbrekend.forEach(function (r) {
          meldFout('variant-bestaat-niet', 'variant', {
            bankprogramma: data.model, elementcode: r.element.code, stofgroep: staat.groep,
            variantsleutel: variantSleutel(r.element.optiewaarde, staat.groep)
          });
        });
        meld('Bestellen is geblokkeerd: voor ' + ontbrekend.map(function (r) { return r.element.naam; }).join(', ') +
             ' bestaat geen variant in deze stofgroep. Meld dit bij ons.', 'fout');
        return;
      }
      /* Laatste vangnet tegen een variantprijs van nul. Shopify rekent serverside
         af op de werkelijke variantprijs; stond die per ongeluk op nul, dan zou de
         bestelling voor nul euro doorgaan. Liever hier geblokkeerd met uitleg. */
      var zonderPrijs = lijst.filter(function (r) { return !geldigePrijs(r.variant); });
      if (zonderPrijs.length) {
        zonderPrijs.forEach(function (r) {
          meldFout('prijs-nul-of-ongeldig', 'prijs', {
            bankprogramma: data.model, elementcode: r.element.code, stofgroep: staat.groep,
            variantsleutel: variantSleutel(r.element.optiewaarde, staat.groep)
          });
        });
        meld('Bestellen is geblokkeerd: voor ' + zonderPrijs.map(function (r) { return r.element.naam; }).join(', ') +
             ' staat geen geldige prijs in Shopify. Meld dit bij de beheerder.', 'fout');
        return;
      }
      var nietBeschikbaar = lijst.filter(function (r) { return r.variant && !r.variant.beschikbaar; });
      if (nietBeschikbaar.length) {
        meld(nietBeschikbaar.map(function (r) { return r.element.naam; }).join(', ') +
             ' is momenteel niet beschikbaar in deze stofgroep.', 'fout');
        return;
      }
      var reeksFout = controleerReeks();
      if (reeksFout) { meld(reeksFout, 'fout'); return; }

      var groepLabel = (data.stofgroepen.filter(function (g) { return g.code === staat.groep; })[0] || {}).label || staat.groep;
      var maat = maatTekst();
      var code = configuratieCode();
      var opmerking = el('opmerking') ? el('opmerking').value.trim() : '';
      var url = deelUrl();

      var artikelen = lijst.map(function (regel, index) {
        var eigenschappen = {
          'Type bestelling': 'Maatwerk bankconfiguratie',
          'Merk': data.merk,
          'Model': data.model,
          'Element': regel.element.naam,
          'Elementcode': regel.element.code,
          'Positie in opstelling': regel.posities.join(', ') + ' van ' + staat.reeks.length + ' (van links geteld)',
          'Stofgroep': groepLabel,
          'Stofnaam': stof.naam,
          'Kleurcode': stof.code,
          '_Configuratie-ID': code,
          '_Configuratielink': url
        };
        if (regel.element.orientatie === 'links' || regel.element.orientatie === 'rechts') {
          eigenschappen['Uitvoering'] = regel.element.orientatie === 'links' ? 'Links' : 'Rechts';
        }
        var maten = [];
        if (getal(regel.element.b)) maten.push(Math.round(getal(regel.element.b)));
        if (getal(regel.element.d)) maten.push(Math.round(getal(regel.element.d)));
        if (getal(regel.element.h)) maten.push(Math.round(getal(regel.element.h)));
        if (maten.length === 3) eigenschappen['Afmetingen'] = maten.join(' × ') + ' cm (b × d × h)';
        if (stof.collectie) eigenschappen['Stofcollectie'] = stof.collectie;
        if (stof.kleurnaam) eigenschappen['Kleurnaam'] = stof.kleurnaam;
        /* De blijvende sleutel en de handle gaan mee, zodat de backoffice altijd
           precies weet welke stof besteld is. De kleurcode alleen is daarvoor
           niet genoeg: die wijst bij SEVN vaak meer dan een stof aan. */
        if (stof.handle) eigenschappen['Stofhandle'] = stof.handle;
        if (stof.configurator_key) eigenschappen['Stofsleutel'] = stof.configurator_key;
        /* Het metaobject-ID apart, zodat de backoffice de stof ook zonder kennis
           van onze sleutelopbouw rechtstreeks in Shopify kan terugvinden. De
           underscore verbergt hem voor de klant in de winkelwagen; op de order
           en in de export blijft hij gewoon staan. */
        if (stof.id) eigenschappen['_Stof-metaobject-ID'] = String(stof.id);
        if (maat) eigenschappen['Buitenmaat opstelling'] = maat;
        if (data.levertijd) eigenschappen['Verwachte levertijd'] = data.levertijd;
        if (opmerking && index === 0) eigenschappen['Opmerking klant'] = opmerking;
        return { id: regel.variant.id, quantity: regel.aantal, properties: eigenschappen };
      });

      var oorspronkelijk = knop.textContent;
      alle('bestel').forEach(function (k) { k.disabled = true; });
      knop.textContent = 'Even geduld…';

      var basis = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(basis + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: artikelen })
      }).then(function (antwoord) {
        if (!antwoord.ok) {
          return antwoord.json().catch(function () { return {}; }).then(function (r) {
            throw new Error(r.description || r.message || 'De samenstelling kon niet worden toegevoegd.');
          });
        }
        window.location.href = basis + 'cart';
      }).catch(function (fout) {
        meldFout('winkelwagen-mislukt', 'winkelwagen', {
          bankprogramma: data.model, configuratieId: code, stofgroep: staat.groep,
          toelichting: fout && fout.message ? fout.message : 'onbekende fout'
        });
        /* De klant krijgt een begrijpelijke zin, niet de technische tekst. */
        meld('De samenstelling kon niet aan de winkelwagen worden toegevoegd. ' +
             'Probeer het opnieuw, of neem contact met ons op.', 'fout');
        knop.textContent = oorspronkelijk;
        tekenOverzicht();
      });
    };

    /* ------------------------------------------------------------------ *
     * Alles bijwerken
     * ------------------------------------------------------------------ */

    var werkAlles = function () {
      tekenElementgroepen();
      tekenElementen();
      tekenStofgroepen();
      tekenStoffilters();
      tekenStoffen();
      tekenOpstelling();
      tekenVolgorde();
      tekenOverzicht();
      werkStapstatus();
      bewaar();
      /* De beheerdersmelding hangt af van gegevens die pas na het opstarten
         binnenkomen: de resterende varianten (pagina 2 en verder) en de
         stoffenbibliotheek. Werd hij alleen bij het opstarten bepaald, dan bleef
         er een onjuiste melding staan — "C: 50 van 61", terwijl alle 61 stalen
         allang geladen waren, en dat blok is voor de klant zichtbaar. Daarom
         hier opnieuw bepalen. Bij de allereerste aanroep bestaat werkBeheer nog
         niet; die wordt verderop toegekend en roept zichzelf dan aan. */
      if (typeof werkBeheer === 'function') werkBeheer();
      /* Als laatste, ná bewaar() en de hertekening: de abonnees. Zo ziet een
         meekijkende laag altijd de al-opgeslagen, consistente toestand. Een
         fout in een abonnee mag deze configurator nooit meeslepen. */
      abonnees.forEach(function (cb) { try { cb(); } catch (e) { /* stil */ } });
    };

    /* ------------------------------------------------------------------ *
     * Vaste knoppen koppelen
     * ------------------------------------------------------------------ */

    var stapKnoppen = alle('stap');
    stapKnoppen.forEach(function (knop, index) {
      luister(knop, 'click', function () { gaNaarStap(index); });
      luister(knop, 'keydown', function (gebeurtenis) {
        var doel = null;
        if (gebeurtenis.key === 'ArrowRight') doel = (index + 1) % stapKnoppen.length;
        else if (gebeurtenis.key === 'ArrowLeft') doel = (index - 1 + stapKnoppen.length) % stapKnoppen.length;
        else if (gebeurtenis.key === 'Home') doel = 0;
        else if (gebeurtenis.key === 'End') doel = stapKnoppen.length - 1;
        if (doel === null) return;
        gebeurtenis.preventDefault();
        gaNaarStap(doel, true);
      });
    });

    var resetKnop = el('reset');
    luister(resetKnop, 'click', function () {
      bewaarGeschiedenis();
      staat.reeks = [];
      staat.stof = '';
      meld('Opstelling gewist.', 'ok');
      werkAlles();
    });

    var terugKnop = el('ongedaan');
    luister(terugKnop, 'click', function () {
      var vorige = geschiedenis.pop();
      if (!vorige) { meld('Er is niets om ongedaan te maken.', 'let'); return; }
      staat.reeks = vorige.reeks;
      staat.groep = vorige.groep;
      staat.stof = vorige.stof;
      meld('Laatste stap ongedaan gemaakt.', 'ok');
      werkAlles();
    });

    var zoekVeld = el('stofzoek');
    luister(zoekVeld, 'input', function () {
      stoffilter.zoek = this.value;
      tekenStoffilters();
      tekenStoffen();
    });

    var favorietKnop = el('filter-favoriet');
    luister(favorietKnop, 'click', function () {
      stoffilter.alleenFavoriet = !stoffilter.alleenFavoriet;
      tekenStoffilters();
      tekenStoffen();
    });

    alle('bestel').forEach(function (knop) {
      luister(knop, 'click', function () { naarWinkelwagen(knop); });
    });

    var deelKnop = el('deel');
    luister(deelKnop, 'click', function () {
      var url = deelUrl();
      window.history.replaceState(null, '', url);
      var klaar = function () { meld('De link naar je samenstelling is gekopieerd.', 'ok'); };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(klaar, function () { meld(url, 'let'); });
      } else {
        var veld = document.createElement('textarea');
        veld.value = url;
        document.body.appendChild(veld);
        veld.select();
        try { document.execCommand('copy'); klaar(); } catch (e) { meld(url, 'let'); }
        veld.remove();
      }
    });

    var printKnop = el('print');
    luister(printKnop, 'click', function () { window.print(); });

    var offerteKnop = el('offerte');
    luister(offerteKnop, 'click', function () {
      var lijst = regels();
      var stof = huidigeStof();
      var totaal = totaalPrijs();
      var maat = maatTekst();
      var tekst = [
        'Offerteaanvraag ' + data.merk + ' ' + data.model,
        'Configuratiecode: ' + configuratieCode(),
        '',
        'Elementen:',
        lijst.map(function (r) { return '- ' + r.aantal + '× ' + r.element.naam + ' (' + r.element.code + ')'; }).join('\n'),
        '',
        maat ? 'Buitenmaat opstelling: ' + maat : '',
        stof ? 'Stof: ' + stof.naam + ' ' + stof.code : 'Stof: nog niet gekozen',
        staat.groep ? 'Stofgroep: ' + staat.groep : '',
        totaal == null ? '' : 'Totaalprijs: ' + geld(totaal),
        data.levertijd ? 'Verwachte levertijd: ' + data.levertijd : '',
        '',
        'Samenstelling: ' + deelUrl()
      ].filter(Boolean).join('\n');

      if (data.contactEmail) {
        window.location.href = 'mailto:' + encodeURIComponent(data.contactEmail) +
          '?subject=' + encodeURIComponent('Offerteaanvraag ' + data.model) +
          '&body=' + encodeURIComponent(tekst);
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(tekst);
        meld('De samenstelling is gekopieerd. Plak hem in het contactformulier.', 'ok');
      }
    });

    /* Deze knop staat buiten de sectie. Zonder opruimen zou er bij elke herlading
       in de themabewerker een extra luisteraar op blijven staan. */
    var startKnop = document.querySelector('[data-lf-naar-configurator]');
    var naarConfigurator = function (gebeurtenis) {
      gebeurtenis.preventDefault();
      wortel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      gaNaarStap(0);
    };
    luister(startKnop, 'click', naarConfigurator);
    opruimTaken.push(function () { clearTimeout(tekstTimer); clearTimeout(verbergTimer); });

    /* ------------------------------------------------------------------ *
     * Opstarten
     * ------------------------------------------------------------------ */

    if (data.stofgroepen.length === 1) staat.groep = data.stofgroepen[0].code;
    herstel();
    tekenPresets();
    gaNaarStap(0);
    werkAlles();
    haalRestOpVarianten();
    laadStoffenBibliotheek();

    /* Beheerdersmeldingen bij onvolledige of onverkoopbare data.

       Dit is bewust een functie en geen eenmalige berekening bij het opstarten.
       Twee bronnen komen namelijk pas later binnen: de varianten voorbij de
       eerste 250 die Liquid kan leveren, en de stoffenbibliotheek uit het
       gegenereerde JSON-bestand. Werd de melding alleen bij het opstarten
       bepaald, dan stond er blijvend iets onwaars — bij Amarillo "Er ontbreken
       25 van de 275 combinaties" en "C: 50 van 61" — terwijl beide een paar
       honderd milliseconden later compleet waren. Dat blok staat in de pagina en
       is dus ook voor de klant zichtbaar. werkAlles roept dit nu na elke
       herberekening aan. */
    var afkappingGemeld = {};
    var werkBeheer = function () {
      var beheerHouder = el('beheer');
      if (!beheerHouder) return;
      var punten = [];
      if (allesOnbeschikbaar) {
        punten.push('Geen enkele variant van dit model staat op verkoopbaar. Zet in Shopify per variant ' +
          '"Doorgaan met verkopen wanneer niet op voorraad" aan, of schakel voorraad bijhouden uit. ' +
          'Zolang dat niet gebeurt kan de klant wel samenstellen maar niet bestellen.');
      }
      /* De vergelijking gaat nu tegen het werkelijke aantal varianten dat de
         server meldt, niet tegen wat Liquid in één keer kon leveren. */
      var geleverd = data.varianten.length;
      if (verwachteCombinaties > geleverd) {
        punten.push('Er ontbreken ' + (verwachteCombinaties - geleverd) + ' van de ' + verwachteCombinaties +
          ' combinaties element × stofgroep. Maak die varianten aan in Shopify, of haal de betreffende ' +
          'stofgroep uit dit bankprogramma.');
      }
      /* Stille afkapping van een stoffenlijst zichtbaar maken. Liquid lost van een
         verwijzingenlijst maar 50 records op terwijl .size het echte aantal
         meldt; het verschil verdwijnt anders geruisloos uit de winkel. */
      if (data.stoffenOpgegeven) {
        var tekort = [];
        Object.keys(data.stoffenOpgegeven).forEach(function (code) {
          var opgegeven = Number(data.stoffenOpgegeven[code]) || 0;
          var geladen = data.stoffen.filter(function (s) { return s.groep === code; }).length;
          if (opgegeven > geladen) {
            tekort.push(code + ': ' + geladen + ' van ' + opgegeven);
            /* Eén keer per stofgroep melden. Deze functie draait nu na elke
               herberekening, en een foutmelding per klik zou het foutenlog
               onbruikbaar maken. */
            if (!afkappingGemeld[code]) {
              afkappingGemeld[code] = true;
              meldFout('stoffen-afgekapt', 'laden', {
                bankprogramma: data.model, stofgroep: code,
                toelichting: geladen + ' van ' + opgegeven + ' stoffen opgelost'
              });
            }
          }
        });
        if (tekort.length) {
          punten.push('Niet alle stoffen zijn geladen — ' + tekort.join(', ') + '. Shopify lost per ' +
            'verwijzingenlijst maar 50 records op. Deze kleuren zijn voor de klant onzichtbaar.');
        }
      }
      if (Object.keys(geblokkeerdeGroepen).length) {
        punten.push('De stoffenbibliotheek kon niet worden geladen. Geblokkeerde stofgroepen: ' +
          Object.keys(geblokkeerdeGroepen).join(', ') + '. Controleer het gegenereerde JSON-bestand.');
      }
      if (variantStatus === 'onvolledig') {
        punten.push('Niet alle varianten konden worden opgehaald: ' + geleverd + ' van ' +
          data.variantenTotaal + '. Bestellen is daarom geblokkeerd.');
      }
      var nulPrijzen = data.varianten.filter(function (v) {
        var b = Number(v.prijs);
        return !isFinite(b) || b <= 0;
      });
      if (nulPrijzen.length) {
        punten.push('Er staan ' + nulPrijzen.length + ' varianten met een prijs van nul of zonder prijs. ' +
          'Die zijn niet te kiezen en niet te bestellen. Vul de prijzen in Shopify aan.');
      }
      if (!data.stoffen.length) {
        punten.push('Voor dit merk staan nog geen stofstalen in Shopify. De klant kan wel een stofgroep ' +
          'kiezen maar geen kleur, en bestellen blijft geblokkeerd.');
      }
      if (punten.length) {
        beheerHouder.hidden = false;
        beheerHouder.innerHTML = '<b>Let op voor de beheerder</b>Dit bericht verschijnt alleen zolang de ' +
          'gegevens niet compleet zijn.<ul>' +
          punten.map(function (p) { return '<li>' + esc(p) + '</li>'; }).join('') + '</ul>';
      } else {
        /* Alsnog compleet geladen: melding weg. Zonder deze tak bleef een
           terechte melding van een seconde eerder voor altijd staan. */
        beheerHouder.hidden = true;
        beheerHouder.innerHTML = '';
      }
    };

    werkBeheer();

    /* ------------------------------------------------------------------ *
     * Brug-API voor een tweede weergavelaag (de 3D-viewer)
     *
     * Bewust op het wortelelement en niet op window: elke sectie-instantie
     * heeft zijn eigen closure-staat, en een paginabrede API zou twee
     * configurators op één pagina door elkaar halen.
     *
     * zetReeks staat uitsluitend HERORDENEN van dezelfde elementen toe en is
     * daarmee strenger dan de eigen pijltjesknoppen (die tijdelijk een
     * ongeldige volgorde toestaan, met melding). Voor een sleepgebaar in 3D
     * is weigeren mét de fouttekst als antwoord de veiligste vorm.
     * ------------------------------------------------------------------ */
    wortel.lfBank = {
      haalReeks: function () { return staat.reeks.slice(); },
      haalStof: function () {
        var s = huidigeStof();
        return {
          groep: staat.groep,
          stof: s ? JSON.parse(JSON.stringify(s)) : null,
          fotoUrl: s && s.foto ? s.foto : ''
        };
      },
      haalElementen: function () {
        return reeksElementen().map(function (e) { return JSON.parse(JSON.stringify(e)); });
      },
      /* Exact dezelfde plaatsing (x, y in cm, hoek in graden) als de
         2D-tekening, uit hetzelfde algoritme. De 3D-laag rekent dus nooit
         zelf een opstelling uit die van de tekening kan afwijken. */
      haalOpstelling: function () { return bouwOpstelling(loopElementen()); },
      zetReeks: function (nieuw) {
        if (!Array.isArray(nieuw) || nieuw.length !== staat.reeks.length) return 'De volgorde klopt niet met de gekozen elementen.';
        if (nieuw.slice().sort().join('') !== staat.reeks.slice().sort().join('')) {
          return 'Alleen de volgorde mag veranderen; elementen toevoegen of weghalen gaat via de elementenlijst.';
        }
        var fout = controleerReeks(nieuw);
        if (fout) return fout;
        bewaarGeschiedenis();
        staat.reeks = nieuw.slice();
        werkAlles();
        return '';
      },
      abonneer: function (cb) {
        abonnees.push(cb);
        return function () {
          var i = abonnees.indexOf(cb);
          if (i > -1) abonnees.splice(i, 1);
        };
      }
    };
    opruimTaken.push(function () { abonnees.length = 0; delete wortel.lfBank; });
    wortel.dispatchEvent(new CustomEvent('lf-bank:klaar', { bubbles: true }));
  };

  var startAlles = function (bereik) {
    (bereik || document).querySelectorAll('[data-lf-bank]').forEach(start);
  };

  var stopAlles = function (bereik) {
    for (var i = opruimers.length - 1; i >= 0; i--) {
      var post = opruimers[i];
      var binnen = !bereik || bereik === post.wortel ||
                   (bereik.contains && bereik.contains(post.wortel));
      if (!binnen) continue;
      post.uitvoeren();
      opruimers.splice(i, 1);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { startAlles(document); });
  } else {
    startAlles(document);
  }
  document.addEventListener('shopify:section:unload', function (gebeurtenis) {
    stopAlles(gebeurtenis.target);
  });
  document.addEventListener('shopify:section:load', function (gebeurtenis) {
    stopAlles(gebeurtenis.target);
    startAlles(gebeurtenis.target);
  });
})();
